import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { runAppDataFetch, DEFAULT_APP_FETCH_MS } from '../../lib/appDataFetch.js';
import { useAppSession } from '../../lib/appSessionBridge.js';
import { Colors as C } from '../../constants/Colors.js';
import WebTopBar from '../components/WebTopBar.js';
import Icon from '../components/Icon.js';
import { Avatar, DCard } from '../components/MarketplaceAtoms.js';
import { profileInitials } from '../../lib/profileAvatar.js';
import TopGroovesRanking from '../components/TopGroovesRanking.js';
import ComunidadMusicGenresField from '../components/ComunidadMusicGenresField.jsx';
import { comunidadMusicGenresSearchText, validateComunidadMusicGenres } from '../../lib/comunidadMusicGenres.js';
import {
  startOfToday,
  getComunidadEventDate,
  comunidadEventDateSortKey,
} from '../../lib/comunidadEventDate.js';
import { supabase } from '../../lib/supabase.js';
import { loadComunidadGuardadosSet, insertComunidadGuardado, deleteComunidadGuardado } from '../../lib/comunidadGuardados.js';
import {
  TIPO_EVENTO,
  COMUNIDAD_TIPOS_PUBLICACION,
  fetchComunidadPublicaciones,
  insertComunidadPublicacion,
  updateComunidadPublicacion,
  deleteComunidadPublicacion,
  adminDeleteComunidadPublicacion,
  setComunidadPublicacionHidden,
  solicitarDestacadoComunidad,
  cancelarSolicitudDestacadoComunidad,
  eventToEditForm,
} from '../../lib/comunidadPublicaciones.js';
import {
  comunidadModeracionEstado,
  comunidadModeracionEstadoPillStyle,
} from '../../lib/comunidadModeracionEstado.js';
import { resolveComunidadImagenUrlForSave } from '../../lib/comunidadImagenesService.js';
import { compressImageForComunidad } from '../../lib/storageService.js';
import { GROOVES, GROOVES_PHONE_GATE_USER_MESSAGE } from '../constants/groovesEconomy.js';
import { moderateText } from '../../lib/moderationService.js';
import { PAISES_COMUN } from '../data/spanishProvinces.js';
import UbicacionSelector from '../components/UbicacionSelector.jsx';
import ComunidadPosterLightbox from '../components/ComunidadPosterLightbox.jsx';
import AdSlot from '../components/AdSlot.jsx';
import {
  fetchActiveAdsBySlot,
  mapAdToComunidadHeroSlide,
  trackAdClick,
  trackAdImpression,
} from '../../lib/adsService.js';
import { useBreakpoints } from '../hooks/useBreakpoints.js';
import {
  fetchSorteosComunidad,
  sorteoCelebrado,
  fetchMisSorteosInscripciones,
  fetchProfileGrooves,
  inscribirSorteoRpc,
  youtubeEmbedUrl,
} from '../../lib/sorteosService.js';
import { fetchPartnersPublicLugares } from '../../lib/lugaresPublicosService.js';
import { fetchReceivedReviews } from '../../lib/reviewsService.js';
import SurcosRating from '../components/SurcosRating.js';
import { PARTNER_ROLE_LABELS } from '../constants/partnerRoleDefs.js';
import { PARTNER_SCHEDULE_OPTIONS } from '../constants/partnerSchedule.js';
import { buildPublicContentUrl } from '../../lib/socialPublishKit.js';
import { isValidComunidadEntradaUrl, tryAutoVerifyEntradaAfterSave } from '../../lib/comunidadEntradaVerify.js';
import { triggerComunidadDuplicateCheckAfterPublish } from '../../lib/comunidadDuplicateCheck.js';
import { fetchPartnerTicketSalesEligibility } from '../../lib/partnerTicketSales.js';

const ENTRADA_TIPO_OPCIONES = [
  { v: 'libre', l: 'Libre / Gratuito' },
  { v: 'consumicion', l: 'Con consumición' },
  { v: 'precio_fijo', l: 'Precio fijo' },
  { v: 'invitacion', l: 'Por invitación' },
  { v: 'donacion', l: 'Donación voluntaria' },
  { v: 'por_confirmar', l: 'Precio de entrada por confirmar' },
  { v: 'otros', l: 'Otros' },
];

const CHIPS = ['Todo','Hoy','Este finde','Gratis','Oficial','Comunidad'];

const MAX_IMAGEN_BYTES = 3 * 1024 * 1024; // 3 MB
const IMAGEN_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';

/** Cabecera de tarjeta en el feed Comunidad: hueco fijo; imagen completa (letterbox) sin deformar. */
const COMUNIDAD_FEED_MEDIA_HEIGHT = 132;
const comunidadFeedMediaWrap = {
  width: '100%',
  height: COMUNIDAD_FEED_MEDIA_HEIGHT,
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'flex-end',
  padding: '10px 14px',
  background: '#0a0a12',
  lineHeight: 0,
};
const comunidadFeedMediaImg = {
  position: 'absolute',
  left: 0,
  top: 0,
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  objectPosition: 'center center',
  display: 'block',
  pointerEvents: 'none',
};
/** Etiqueta tipo (Comunidad / Oficial…) sobre la imagen del feed — padding generoso para no pegar al borde del óvalo. */
const comunidadFeedTagPill = {
  display: 'inline-block',
  background: C.gold,
  color: '#0a0a0f',
  fontSize: 9,
  fontWeight: 600,
  lineHeight: 1.35,
  padding: '5px 12px',
  borderRadius: 999,
  letterSpacing: '0.02em',
  boxSizing: 'border-box',
};

function isEventToday(ev) {
  const d = getComunidadEventDate(ev);
  if (!d) return false;
  return d.getTime() === startOfToday().getTime();
}

/** Sábado y domingo del fin de semana de la semana en curso. */
function isEventThisWeekend(ev) {
  const d = getComunidadEventDate(ev);
  if (!d) return false;
  const today = startOfToday();
  const dow = today.getDay();
  const sat = new Date(today);
  const sun = new Date(today);
  if (dow === 0) {
    sat.setDate(today.getDate() - 1);
  } else if (dow === 6) {
    sun.setDate(today.getDate() + 1);
  } else {
    sat.setDate(today.getDate() + (6 - dow));
    sun.setDate(sat.getDate() + 1);
  }
  sat.setHours(0, 0, 0, 0);
  sun.setHours(0, 0, 0, 0);
  const t = d.getTime();
  return t === sat.getTime() || t === sun.getTime();
}

function eventMatchesChip(ev, chip) {
  if (chip === 'Todo' || !chip) return true;
  if (chip === 'Oficial') return /oficial/i.test(ev.tag) || ev.type === 'Oficial';
  if (chip === 'Comunidad') return ev.type === 'Comunidad' && /comunidad/i.test(ev.tag);
  if (chip === 'Gratis') {
    return /gratis|libre|donación|donacion/i.test(String(ev.price)) || ev.entrada_tipo === 'libre' || ev.entrada_tipo === 'donacion';
  }
  if (chip === 'Hoy') return isEventToday(ev);
  if (chip === 'Este finde') return isEventThisWeekend(ev);
  return true;
}

function isPastEvent(ev) {
  const d = getComunidadEventDate(ev);
  if (!d) return false; // si no se puede parsear, no lo escondemos del listado principal
  return d.getTime() < startOfToday().getTime();
}

function historicoTypeMatch(ev, tipo) {
  if (!tipo || tipo === 'Todo') return true;
  if (tipo === 'Oficial') return /oficial/i.test(ev.tag) || ev.type === 'Oficial';
  if (tipo === 'Comunidad') return ev.type === 'Comunidad' && /comunidad/i.test(ev.tag);
  if (tipo === 'Lugar') return ev.type === 'Lugar';
  return true;
}

function eventMatchesSearch(ev, q) {
  if (!q.trim()) return true;
  const s = q.trim().toLowerCase();
  const blob = [
    ev.title,
    ev.place,
    ev.type,
    ev.tag,
    ev.date,
    String(ev.price),
    ev.mapsUrl || '',
    ev.entrada_tipo || '',
    ev.entrada_precio || '',
    ev.authorUsername || '',
    comunidadMusicGenresSearchText(ev.musicGenres),
  ]
    .join(' ')
    .toLowerCase();
  return blob.includes(s);
}

function ComunidadGenrePills({ genres, compact }) {
  const list = Array.isArray(genres) ? genres : [];
  if (!list.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: compact ? 4 : 5, marginBottom: compact ? 4 : 6 }}>
      {list.map(g => (
        <span
          key={g}
          style={{
            fontSize: compact ? 9 : 10,
            padding: compact ? '2px 7px' : '3px 8px',
            borderRadius: 8,
            background: 'rgba(200,168,75,0.1)',
            border: '0.5px solid rgba(200,168,75,0.28)',
            color: 'rgba(230,208,140,0.95)',
            fontWeight: 600,
          }}
        >
          {g}
        </span>
      ))}
    </div>
  );
}

function ComunidadSearchInput({ value, onChange, placeholder, ariaLabel }) {
  const [focused, setFocused] = useState(false);
  const borderColor = focused ? C.gold : 'rgba(200,168,75,0.38)';
  return (
    <div style={{ position: 'relative', marginBottom: 14 }}>
      <span
        style={{
          position: 'absolute',
          left: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center',
          pointerEvents: 'none',
        }}
        aria-hidden
      >
        <Icon name="search" size={17} color={focused ? C.gold : 'rgba(200,168,75,0.82)'} />
      </span>
      <input
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        aria-label={ariaLabel}
        style={{
          width: '100%',
          padding: '10px 14px 10px 40px',
          borderRadius: 12,
          border: `0.5px solid ${borderColor}`,
          background: 'rgba(255,255,255,0.08)',
          color: '#fff',
          fontSize: 13,
          outline: 'none',
          fontFamily: 'inherit',
          boxSizing: 'border-box',
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
    </div>
  );
}

/**
 * Misma regla que la política RLS `comunidad_select` (moderación): en el feed de tarjetas
 * solo entran aprobadas y no ocultas para el público; pendientes solo autor o admin.
 * Así el mensaje «aún no visible para todos» coincide con la lista aunque el RLS esté relajado.
 */
function comunidadDbEvVisibleInFeed(ev, isAdmin, userId) {
  if (!ev._db) return false;
  if (isAdmin) return true;
  if (ev.isHidden) return false;
  if (ev.aprobada) return true;
  if (userId && ev.authorId === userId) return true;
  return false;
}

function embedReviewerReview(r) {
  const rev = Array.isArray(r.reviewer) ? r.reviewer[0] : r.reviewer;
  return rev ?? {};
}

function ratingHistogramLugar(rows) {
  const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  for (const r of rows) {
    const n = Math.min(5, Math.max(1, Math.round(Number(r.rating) || 0)));
    if (n >= 1 && n <= 5) counts[n] += 1;
  }
  const total = rows.length || 1;
  return [5, 4, 3, 2, 1].map(star => ({
    star,
    pct: Math.round((counts[star] / total) * 100),
  }));
}

function aggregateReviewTagsLugar(rows) {
  const freq = new Map();
  for (const r of rows) {
    const tags = Array.isArray(r.tags) ? r.tags : [];
    for (const t of tags) {
      const k = String(t || '').trim();
      if (!k) continue;
      freq.set(k, (freq.get(k) || 0) + 1);
    }
  }
  return [...freq.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([t]) => t);
}

function scheduleTypeTitle(st) {
  const v = st === '24h' || st === 'special' || st === 'standard' ? st : 'standard';
  return PARTNER_SCHEDULE_OPTIONS.find(o => o.value === v)?.title ?? v;
}

function mergePhotoGallery(logoUrl, photosUrls) {
  const pics = [];
  const seen = new Set();
  const add = u => {
    const s = String(u || '').trim();
    if (!s || seen.has(s)) return;
    seen.add(s);
    pics.push(s);
  };
  add(logoUrl);
  if (Array.isArray(photosUrls)) photosUrls.forEach(add);
  return pics;
}

/** Asegura href https para abrir en nueva pestaña */
function hrefMaps(url) {
  if (!url || !String(url).trim()) return '#';
  const t = String(url).trim();
  if (/^https?:\/\//i.test(t)) return t;
  if (/^maps\./i.test(t) || /^goo\.gl/i.test(t) || t.includes('google.com/maps') || t.includes('maps.app')) {
    return `https://${t.replace(/^\/+/, '')}`;
  }
  return t.startsWith('//') ? `https:${t}` : `https://${t}`;
}

function hrefTicket(url) {
  if (!url || !String(url).trim()) return '#';
  return hrefMaps(url);
}

/** Enlace canónico a la publicación (producción: `https://dreamvinyl.es/?comunidad=`). */
function buildComunidadItemShareUrl(evId) {
  return buildPublicContentUrl('comunidad', evId);
}

function sorteoInscripcionAbierta(row) {
  if (!row || row.estado !== 'publicado') return false;
  const now = Date.now();
  const desde = row.inscripciones_desde ? new Date(row.inscripciones_desde).getTime() : NaN;
  const hasta = row.inscripciones_hasta ? new Date(row.inscripciones_hasta).getTime() : NaN;
  if (!Number.isFinite(desde) || !Number.isFinite(hasta)) return false;
  return now >= desde && now <= hasta;
}

/** Requisito producto + RLS `comunidad_insert` (admins exentos en servidor vía `profiles.is_admin`). */
function comunidadPublishIdentityMessage(emailOk, phoneOk) {
  const parts = [];
  if (!emailOk) {
    parts.push('confirmar tu correo electrónico (revisa la bandeja de entrada o pide un nuevo enlace desde iniciar sesión)');
  }
  if (!phoneOk) {
    parts.push('verificar tu número de móvil por SMS en Ajustes → Cuenta');
  }
  if (!parts.length) return '';
  return `Para publicar en Comunidad necesitas ${parts.join(' y ')}. Abre Ajustes → Cuenta desde el menú de tu perfil.`;
}

/**
 * Resuelve permisos en el momento (evita condición de carrera con `is_admin` cargado async en estado).
 * Debe coincidir con la política RLS `comunidad_insert` / `comunidad_update_author`.
 */
async function resolveComunidadPublishGate(user) {
  if (!user?.id) {
    return {
      allowed: false,
      isAdmin: false,
      emailOk: false,
      phoneOk: false,
      groovesBalance: null,
      message: 'Inicia sesión para publicar.',
    };
  }
  const { data: prof, error } = await supabase.from('profiles').select('is_admin').eq('id', user.id).maybeSingle();
  if (error) {
    return {
      allowed: false,
      isAdmin: false,
      emailOk: !!user.email_confirmed_at,
      phoneOk: false,
      groovesBalance: null,
      message: 'No se pudo comprobar tu cuenta. Inténtalo de nuevo en unos segundos.',
    };
  }
  const isAdmin = !!prof?.is_admin;
  if (isAdmin) {
    return { allowed: true, isAdmin: true, emailOk: true, phoneOk: true, groovesBalance: null, message: null };
  }
  const emailOk = !!user.email_confirmed_at;
  const w = await fetchProfileGrooves(user.id);
  const phoneOk = !!w?.phoneVerified;
  const groovesBalance = typeof w?.balance === 'number' ? w.balance : null;
  if (emailOk && phoneOk) {
    return { allowed: true, isAdmin: false, emailOk, phoneOk, groovesBalance, message: null };
  }
  return {
    allowed: false,
    isAdmin: false,
    emailOk,
    phoneOk,
    groovesBalance,
    message: comunidadPublishIdentityMessage(emailOk, phoneOk),
  };
}

function mapComunidadSaveError(err) {
  const raw = String(err?.message || err || '');
  const code = String(err?.code || '');
  if (/partner_ticket_sales_required|solo partners verificados con suscripción/i.test(raw)) {
    return 'Solo partners verificados con suscripción activa pueden publicar enlaces de venta de entradas. Publica el evento sin enlace o contacta con DreamVinyl.';
  }
  if (/row-level security|violates row-level security policy|42501/i.test(raw) || code === '42501') {
    return 'No se pudo guardar por permisos de base de datos. Si deberías poder publicar: en Supabase, revisa que tu fila en `profiles` tenga `is_admin = true` (administradores no requieren teléfono verificado). El resto de usuarios necesitan correo confirmado y móvil verificado en Ajustes → Cuenta.';
  }
  if (code === 'check_violation' && /entradas/i.test(raw)) return raw;
  return raw || 'No se pudo guardar.';
}

function mensajeRpcSorteo(code) {
  const map = {
    not_auth: 'Tienes que iniciar sesión para participar.',
    insufficient_grooves: 'No tienes suficientes grooves para esta entrada.',
    already_enrolled: 'Ya estás inscrito/a en este sorteo.',
    inscriptions_closed: 'Las inscripciones ya están cerradas.',
    inscriptions_not_started: 'Las inscripciones aún no han empezado.',
    not_found: 'Este sorteo no existe o fue retirado.',
    not_open_state: 'Este sorteo no admite nuevas inscripciones.',
  };
  return map[code] || 'No se pudo completar la inscripción. Inténtalo de nuevo.';
}

const pillGr = { display: 'inline-block', fontSize: 10, padding: '3px 10px', borderRadius: 8, background: 'rgba(93,202,165,0.15)', color: '#5DCAA5', border: '1px solid rgba(93,202,165,0.3)' };
const pillCo = { display: 'inline-block', fontSize: 10, padding: '3px 10px', borderRadius: 8, background: 'rgba(200,168,75,0.15)', color: '#c8a84b', border: '1px solid rgba(200,168,75,0.3)' };
const pillFi = { display: 'inline-block', fontSize: 10, padding: '3px 10px', borderRadius: 8, background: 'rgba(10,10,15,0.6)', color: '#e8edf2', border: '1px solid #1e2d3d' };
const pillRx = { display: 'inline-block', fontSize: 10, padding: '3px 10px', borderRadius: 8, background: 'rgba(10,10,15,0.6)', color: '#8899aa', border: '1px solid #1e2d3d' };

function coalesceEntradasVerif(v) {
  return v === true;
}

function renderEntradaTipoPill(ev) {
  const t = ev.entrada_tipo;
  const p = ev.entrada_precio;
  if (t == null) return null;
  if (t === 'libre') return <span style={pillGr}>Gratis</span>;
  if (t === 'consumicion') return <span style={pillCo}>Con consumición</span>;
  if (t === 'precio_fijo') {
    return <span style={pillFi}>Precio fijo{p && ` · ${p}`}</span>;
  }
  if (t === 'invitacion') return <span style={pillRx}>Por invitación</span>;
  if (t === 'donacion') return <span style={pillRx}>Donación voluntaria</span>;
  if (t === 'por_confirmar') return <span style={pillRx}>Precio por confirmar</span>;
  if (t === 'otros') return <span style={pillRx}>{p && p.trim() ? p : 'Otros'}</span>;
  return <span style={pillRx}>-</span>;
}

const labelForm = { fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' };

export default function PageComunidad({
  onBack,
  onNavigate,
  user,
  onRequireAuth,
  deepLinkComunidadId,
  onConsumedDeepLinkComunidad,
}) {
  const { authReady, dataRefreshKey } = useAppSession();
  const { isMobile, isTablet } = useBreakpoints();
  const [dbEvents, setDbEvents] = useState([]);
  const [feedInitialDone, setFeedInitialDone] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [feedError, setFeedError] = useState(null);
  const [comunidadPublishBlockHint, setComunidadPublishBlockHint] = useState(null);
  const [publishNotice, setPublishNotice] = useState(null);
  const publishNoticeTimerRef = useRef(null);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [reactions, setReactions] = useState({});
  const [search, setSearch] = useState('');
  const [activeChip, setActiveChip] = useState('Todo');
  const [showHistorico, setShowHistorico] = useState(false);
  const [historicoTipo, setHistoricoTipo] = useState('Todo'); // Todo | Oficial | Comunidad | Lugar
  const [publicarOpen, setPublicarOpen] = useState(false);
  const defaultForm = () => ({
    titulo: '',
    tipo: 'Evento en vivo',
    pais: 'ES',
    provincia: '',
    provinciaTexto: '',
    ciudad: '',
    via: '',
    cp: '',
    mapsUrl: '',
    fecha: '',
    entradaTipo: 'libre',
    entradaDetalle: '',
    entradaEnlace: '',
    notas: '',
    imagenPreview: null,
    musicGenreMode: 'specific',
    musicGenres: [],
  });
  const [form, setForm] = useState(() => defaultForm());
  const [imagenError, setImagenError] = useState('');
  const [imagenPendingFile, setImagenPendingFile] = useState(null);
  const [imagenPreparing, setImagenPreparing] = useState(false);
  const imagenBlobUrlRef = useRef(null);
  const fileInputRef = useRef(null);
  const [shareCopiedId, setShareCopiedId] = useState(null);
  const [posterLightbox, setPosterLightbox] = useState(null);
  const shareToastTimerRef = useRef(null);
  const [savedItemIds, setSavedItemIds] = useState(() => new Set());
  const [savingId, setSavingId] = useState(null);
  const [featuredHeroIdx, setFeaturedHeroIdx] = useState(0);
  const [heroFeaturedOpaque, setHeroFeaturedOpaque] = useState(true);
  const [comunidadHeroAds, setComunidadHeroAds] = useState([]);
  const lastComunidadHeroAdImpressedRef = useRef(null);
  const [partnerApproved, setPartnerApproved] = useState(false);
  const [ticketSalesEligibility, setTicketSalesEligibility] = useState({
    loading: false,
    eligible: false,
    reason: '',
  });
  const [destacBusyId, setDestacBusyId] = useState(null);
  const [sorteosList, setSorteosList] = useState([]);
  const [sorteosLoading, setSorteosLoading] = useState(false);
  const [sorteosError, setSorteosError] = useState(null);
  const [sorteoEnrolledIds, setSorteoEnrolledIds] = useState(() => new Set());
  const [userGroovesComunidad, setUserGroovesComunidad] = useState(null);
  const [groovesComunidadPhoneOk, setGroovesComunidadPhoneOk] = useState(true);
  const [sorteoInscribirBusyId, setSorteoInscribirBusyId] = useState(null);

  /** `feed` = eventos y publicaciones; `lugares` = directorio verificado venue/store/pickup */
  const [comunidadTab, setComunidadTab] = useState('feed');
  const [lugaresRows, setLugaresRows] = useState([]);
  const [lugaresLoading, setLugaresLoading] = useState(false);
  const [lugaresErr, setLugaresErr] = useState(null);
  const [lugaresSearch, setLugaresSearch] = useState('');
  const [lugarFicha, setLugarFicha] = useState(null);
  const [lugarReviews, setLugarReviews] = useState([]);
  const [lugarReviewsLoading, setLugarReviewsLoading] = useState(false);
  const [lugarGalleryIdx, setLugarGalleryIdx] = useState(0);

  const reloadFeed = useCallback(async () => {
    try {
      setFeedError(null);
      const rows = await runAppDataFetch(() => fetchComunidadPublicaciones({ includeHidden: !!isAdmin }), {
        timeoutMs: DEFAULT_APP_FETCH_MS,
        timeoutMessage: 'Tiempo de espera al cargar publicaciones. Revisa la conexión o pulsa Recargar.',
      });
      setDbEvents(rows);
    } catch (e) {
      setFeedError(e?.message || 'No se pudieron cargar las publicaciones.');
      setDbEvents([]);
    } finally {
      setFeedInitialDone(true);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!authReady) return;
    reloadFeed();
  }, [authReady, dataRefreshKey, reloadFeed]);

  useEffect(() => {
    if (isAdmin) {
      const ch = supabase
        .channel('comunidad_pub_admin_rt')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'comunidad_publicaciones' },
          () => {
            reloadFeed();
          }
        )
        .subscribe();
      return () => {
        supabase.removeChannel(ch);
      };
    }
    if (!user?.id) return undefined;
    const ch = supabase
      .channel(`comunidad_pub_autor_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'comunidad_publicaciones',
          filter: `author_id=eq.${user.id}`,
        },
        () => {
          reloadFeed();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [isAdmin, user?.id, reloadFeed]);

  const reloadSorteos = useCallback(async () => {
    setSorteosLoading(true);
    setSorteosError(null);
    try {
      const rows = await runAppDataFetch(() => fetchSorteosComunidad(), {
        timeoutMs: DEFAULT_APP_FETCH_MS,
        timeoutMessage: 'Tiempo de espera al cargar sorteos. Revisa la conexión o actualiza la página.',
      });
      setSorteosList(rows);
      if (user?.id) {
        const ids = await runAppDataFetch(() => fetchMisSorteosInscripciones(user.id), {
          timeoutMs: DEFAULT_APP_FETCH_MS,
        });
        setSorteoEnrolledIds(new Set(ids));
        const w = await runAppDataFetch(() => fetchProfileGrooves(user.id), {
          timeoutMs: DEFAULT_APP_FETCH_MS,
        });
        setUserGroovesComunidad(w?.balance ?? null);
        setGroovesComunidadPhoneOk(!!w?.phoneVerified);
      } else {
        setSorteoEnrolledIds(new Set());
        setUserGroovesComunidad(null);
        setGroovesComunidadPhoneOk(true);
      }
    } catch (e) {
      setSorteosError(e?.message || 'No se pudieron cargar los sorteos.');
      setSorteosList([]);
    } finally {
      setSorteosLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!authReady) return;
    reloadSorteos();
  }, [authReady, dataRefreshKey, user?.id, reloadSorteos]);

  useEffect(() => {
    if (!authReady) return undefined;
    const reloadAll = () => {
      reloadFeed();
      reloadSorteos();
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') reloadAll();
    };
    const onOnline = () => reloadAll();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('online', onOnline);
    };
  }, [authReady, reloadFeed, reloadSorteos]);

  useEffect(() => {
    if (!user?.id) setComunidadPublishBlockHint(null);
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) {
        if (!cancelled) setIsAdmin(false);
        return;
      }
      const { data } = await supabase.from('profiles').select('is_admin').eq('id', user.id).maybeSingle();
      if (!cancelled) setIsAdmin(!!data?.is_admin);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const lugaresFiltered = useMemo(() => {
    const q = lugaresSearch.trim().toLowerCase();
    if (!q) return lugaresRows;
    return lugaresRows.filter(row => {
      const blob = [
        row.company_name,
        row.city,
        row.country,
        row.sector,
        ...(Array.isArray(row.music_genres) ? row.music_genres : []),
        ...(Array.isArray(row.place_roles) ? row.place_roles : []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });
  }, [lugaresRows, lugaresSearch]);

  const publicacionesComunidadDelLugar = useMemo(() => {
    if (!lugarFicha?.profile_id) return [];
    const pid = lugarFicha.profile_id;
    return dbEvents.filter(ev => ev._db && ev.authorId === pid && ev.aprobada && !ev.isHidden);
  }, [dbEvents, lugarFicha?.profile_id]);

  const lugarRatingStats = useMemo(() => {
    const rows = lugarReviews;
    const count = rows.length;
    const avg = count ? rows.reduce((s, r) => s + (Number(r.rating) || 0), 0) / count : null;
    const valoracionMedia = avg != null ? Math.round(avg * 10) / 10 : null;
    const valoracionSurcos =
      count > 0 && valoracionMedia != null ? Math.min(5, Math.max(1, Math.round(valoracionMedia))) : 0;
    return {
      count,
      avg: valoracionMedia,
      surcos: valoracionSurcos,
      histogram: ratingHistogramLugar(rows),
      topTags: aggregateReviewTagsLugar(rows),
    };
  }, [lugarReviews]);

  useEffect(() => {
    let cancelled = false;
    fetchActiveAdsBySlot('hero_comunidad_destacados', 12).then(list => {
      if (!cancelled) setComunidadHeroAds(list || []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const heroPool = useMemo(() => {
    const fromAds = (comunidadHeroAds || []).map(mapAdToComunidadHeroSlide).filter(Boolean);
    if (fromAds.length) return fromAds;
    const list = dbEvents.filter(ev => ev._db && ev.aprobada && !ev.isHidden && ev.destacada);
    return [...list].sort((a, b) => {
      const ta = new Date(a.createdAtDb || 0).getTime();
      const tb = new Date(b.createdAtDb || 0).getTime();
      return tb - ta;
    });
  }, [comunidadHeroAds, dbEvents]);

  const heroPoolKey = useMemo(() => heroPool.map(h => String(h.id)).join('|'), [heroPool]);

  useEffect(() => {
    setFeaturedHeroIdx(0);
    setHeroFeaturedOpaque(true);
  }, [heroPoolKey]);

  useEffect(() => {
    if (heroPool.length <= 1) return undefined;
    const id = window.setInterval(() => {
      setHeroFeaturedOpaque(false);
    }, 6000);
    return () => window.clearInterval(id);
  }, [heroPool.length, heroPoolKey]);

  const slidingFeaturedHero = heroPool.length > 1;
  const heroSlide = heroPool.length > 0 ? heroPool[featuredHeroIdx % heroPool.length] : null;
  const heroBlockOpacity = slidingFeaturedHero ? (heroFeaturedOpaque ? 1 : 0) : 1;

  useEffect(() => {
    if (!heroSlide?.isPromoAd || !heroSlide.adId) return;
    if (lastComunidadHeroAdImpressedRef.current === heroSlide.adId) return;
    lastComunidadHeroAdImpressedRef.current = heroSlide.adId;
    trackAdImpression(heroSlide.adId);
  }, [heroSlide?.adId, heroSlide?.isPromoAd]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) {
        if (!cancelled) setPartnerApproved(false);
        return;
      }
      const { data } = await supabase
        .from('partners')
        .select('id')
        .eq('profile_id', user.id)
        .eq('verification_status', 'approved')
        .maybeSingle();
      if (!cancelled) setPartnerApproved(!!data?.id);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) {
        if (!cancelled) setTicketSalesEligibility({ loading: false, eligible: false, reason: '' });
        return;
      }
      if (!cancelled) setTicketSalesEligibility(s => ({ ...s, loading: true }));
      const el = await fetchPartnerTicketSalesEligibility(user.id);
      if (!cancelled) {
        setTicketSalesEligibility({
          loading: false,
          eligible: !!el.eligible,
          reason: el.reason || '',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!authReady || comunidadTab !== 'lugares') return undefined;
    let cancelled = false;
    (async () => {
      setLugaresLoading(true);
      setLugaresErr(null);
      try {
        const rows = await runAppDataFetch(() => fetchPartnersPublicLugares(), {
          timeoutMs: DEFAULT_APP_FETCH_MS,
          timeoutMessage: 'Tiempo de espera al cargar lugares.',
        });
        if (!cancelled) setLugaresRows(rows);
      } catch (e) {
        if (!cancelled) {
          setLugaresErr(e?.message || 'No se pudieron cargar los lugares.');
          setLugaresRows([]);
        }
      } finally {
        if (!cancelled) setLugaresLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authReady, dataRefreshKey, comunidadTab]);

  useEffect(() => {
    if (!lugarFicha?.profile_id) {
      setLugarReviews([]);
      setLugarReviewsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLugarReviewsLoading(true);
      const { data } = await fetchReceivedReviews(lugarFicha.profile_id, { onlyVisible: true, limit: 80 });
      if (!cancelled) setLugarReviews(Array.isArray(data) ? data : []);
      if (!cancelled) setLugarReviewsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [lugarFicha?.profile_id]);

  useEffect(() => {
    setLugarGalleryIdx(0);
  }, [lugarFicha?.partner_id]);

  const scrollToComunidadCard = useCallback(evId => {
    if (typeof document === 'undefined' || evId == null) return;
    const safe = typeof window.CSS !== 'undefined' && typeof window.CSS.escape === 'function'
      ? window.CSS.escape(String(evId))
      : String(evId).replace(/["\\]/g, '');
    document.querySelector(`[data-comunidad-id="${safe}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const openHeroPromoTarget = useCallback(slide => {
    if (!slide?.isPromoAd) return;
    trackAdClick(slide.adId);
    window.open(slide.targetUrl, '_blank', 'noopener,noreferrer');
  }, []);

  const goToHeroPublication = useCallback(
    slide => {
      if (!slide) return;
      if (slide.isPromoAd) {
        openHeroPromoTarget(slide);
        return;
      }
      scrollToComunidadCard(slide.id);
    },
    [openHeroPromoTarget, scrollToComunidadCard]
  );

  const onHeroFeaturedImageClick = useCallback(
    slide => {
      if (!slide) return;
      if (slide.isPromoAd) {
        openHeroPromoTarget(slide);
        return;
      }
      if (slide.imagenUrl) {
        setPosterLightbox({ url: slide.imagenUrl, title: slide.title || null });
      }
    },
    [openHeroPromoTarget]
  );

  const verPublicacionEnFeed = useCallback(
    evId => {
      setComunidadTab('feed');
      setLugarFicha(null);
      window.setTimeout(() => scrollToComunidadCard(evId), 380);
    },
    [scrollToComunidadCard]
  );

  useEffect(() => {
    if (!deepLinkComunidadId || !feedInitialDone) return;
    const hit = dbEvents.find(e => String(e.id) === deepLinkComunidadId);
    if (hit) verPublicacionEnFeed(deepLinkComunidadId);
    onConsumedDeepLinkComunidad?.();
  }, [deepLinkComunidadId, feedInitialDone, dbEvents, verPublicacionEnFeed, onConsumedDeepLinkComunidad]);

  const onCambiarComunidadTab = useCallback(tab => {
    setComunidadTab(tab);
    if (tab === 'feed') setLugarFicha(null);
  }, []);

  const showPublishNoticeBrief = useCallback(msg => {
    if (publishNoticeTimerRef.current) clearTimeout(publishNoticeTimerRef.current);
    setPublishNotice(msg);
    publishNoticeTimerRef.current = window.setTimeout(() => {
      setPublishNotice(null);
      publishNoticeTimerRef.current = null;
    }, 9000);
  }, []);

  const onSolicitarDestacado = useCallback(
    async ev => {
      if (!user?.id || destacBusyId) return;
      setDestacBusyId(String(ev.id));
      setFeedError(null);
      try {
        const ok = window.confirm(
          `Si la administración marca tu publicación en la portada, se descontarán hasta ${GROOVES.COMUNIDAD_DESTACAR_PARTNER} grooves de tu cuenta partner. No se cobra nada hasta entonces.\n\n¿Enviar solicitud?`
        );
        if (!ok) return;
        const { error } = await solicitarDestacadoComunidad(ev.id, user.id);
        if (error) throw error;
        await reloadFeed();
        showPublishNoticeBrief('Solicitud enviada. Un administrador decidirá si aparece en la portada destacada.');
      } catch (e) {
        setFeedError(e?.message || 'No se pudo enviar la solicitud');
      } finally {
        setDestacBusyId(null);
      }
    },
    [user?.id, destacBusyId, reloadFeed, showPublishNoticeBrief]
  );

  const onInscribirSorteo = useCallback(
    async row => {
      if (!user?.id) {
        if (typeof onRequireAuth === 'function') onRequireAuth();
        return;
      }
      if (!row?.id || sorteoInscribirBusyId) return;
      setSorteoInscribirBusyId(String(row.id));
      setFeedError(null);
      try {
        const cost = row.modalidad_precio === 'grooves' ? Number(row.grooves_entrada || 0) : 0;
        if (cost > 0 && !groovesComunidadPhoneOk) {
          setFeedError(GROOVES_PHONE_GATE_USER_MESSAGE);
          setSorteoInscribirBusyId(null);
          return;
        }
        if (
          cost > 0 &&
          !window.confirm(
            `Este sorteo cuesta ${cost} grooves por participar. ¿Descontarlo de tu saldo (${userGroovesComunidad ?? '—'} disponibles)?`
          )
        ) {
          setSorteoInscribirBusyId(null);
          return;
        }
        const res = await inscribirSorteoRpc(row.id);
        if (!res.ok) {
          setFeedError(mensajeRpcSorteo(res.code) || String(res.code || ''));
          return;
        }
        await reloadSorteos();
        showPublishNoticeBrief('¡Ya estás inscrito/a! Te avisará DreamVinyl según vuestras reglas vigentes.');
      } catch (e) {
        setFeedError(e?.message || 'Error al inscribirte');
      } finally {
        setSorteoInscribirBusyId(null);
      }
    },
    [user?.id, sorteoInscribirBusyId, reloadSorteos, showPublishNoticeBrief, onRequireAuth, userGroovesComunidad, groovesComunidadPhoneOk]
  );

  const onCancelarDestacado = useCallback(
    async ev => {
      if (!user?.id || destacBusyId) return;
      setDestacBusyId(String(ev.id));
      setFeedError(null);
      try {
        if (!window.confirm('¿Cancelar la solicitud de portada?')) return;
        const { error } = await cancelarSolicitudDestacadoComunidad(ev.id, user.id);
        if (error) throw error;
        await reloadFeed();
        showPublishNoticeBrief('Solicitud de portada cancelada.');
      } catch (e) {
        setFeedError(e?.message || 'No se pudo cancelar la solicitud');
      } finally {
        setDestacBusyId(null);
      }
    },
    [user?.id, destacBusyId, reloadFeed, showPublishNoticeBrief]
  );

  useEffect(() => {
    return () => {
      if (shareToastTimerRef.current) clearTimeout(shareToastTimerRef.current);
      if (publishNoticeTimerRef.current) clearTimeout(publishNoticeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) {
        if (!cancelled) setSavedItemIds(new Set());
        return;
      }
      const setG = await loadComunidadGuardadosSet(user.id);
      if (!cancelled) setSavedItemIds(setG);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const toggleGuardar = useCallback(
    async ev => {
      if (!user?.id) {
        if (typeof onRequireAuth === 'function') onRequireAuth();
        return;
      }
      const idStr = String(ev.id);
      if (savingId) return;
      setSavingId(idStr);
      const isSaved = savedItemIds.has(idStr);
      try {
        if (isSaved) {
          const { error } = await deleteComunidadGuardado(user.id, idStr);
          if (!error) {
            setSavedItemIds(prev => {
              const n = new Set(prev);
              n.delete(idStr);
              return n;
            });
          }
        } else {
          const { error } = await insertComunidadGuardado(user.id, ev);
          if (!error) {
            setSavedItemIds(prev => new Set(prev).add(idStr));
          }
        }
      } finally {
        setSavingId(null);
      }
    },
    [user, savedItemIds, onRequireAuth, savingId]
  );

  const compartirEvento = useCallback(async ev => {
    const url = buildComunidadItemShareUrl(ev.id);
    const title = `DreamVinyl — ${ev.title}`;
    const text = [ev.title, ev.place, ev.date].filter(Boolean).join(' · ');
    const notificarEnlaceCopiado = () => {
      if (shareToastTimerRef.current) clearTimeout(shareToastTimerRef.current);
      setShareCopiedId(ev.id);
      shareToastTimerRef.current = setTimeout(() => setShareCopiedId(null), 2500);
    };

    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      const rich = { title, text, url };
      const plain = { text: text ? `${text}\n${url}` : url };
      try {
        if (typeof navigator.canShare === 'function') {
          if (navigator.canShare(rich)) {
            await navigator.share(rich);
            return;
          }
          if (navigator.canShare(plain)) {
            await navigator.share(plain);
            return;
          }
        } else {
          await navigator.share(rich);
          return;
        }
      } catch (e) {
        if (e && e.name === 'AbortError') return;
        try {
          if (typeof navigator.canShare !== 'function' || navigator.canShare(plain)) {
            await navigator.share(plain);
            return;
          }
        } catch (e2) {
          if (e2 && e2.name === 'AbortError') return;
        }
      }
    }

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(url);
        notificarEnlaceCopiado();
        return;
      }
    } catch {
      // prompt
    }
    const p = window.prompt('Copia el enlace (Ctrl/Cmd+C) y Aceptar:', url);
    if (p !== null) notificarEnlaceCopiado();
  }, []);

  const toggleReaction = useCallback(key => {
    setReactions(r => ({ ...r, [key]: !r[key] }));
  }, []);

  const filtered = useMemo(() => {
    const base = dbEvents
      .filter(ev => comunidadDbEvVisibleInFeed(ev, isAdmin, user?.id))
      .filter(ev => (showHistorico ? isPastEvent(ev) : !isPastEvent(ev)))
      .filter(ev => eventMatchesSearch(ev, search));

    /** Destacadas primero; luego fecha de evento ascendente (más cercana a hoy arriba). Sin fecha al final. */
    const byDestacaThenEventDate = (a, b) => {
      const w = Number(!!b.destacada) - Number(!!a.destacada);
      if (w !== 0) return w;
      const ta = comunidadEventDateSortKey(a);
      const tb = comunidadEventDateSortKey(b);
      if (ta !== tb) return ta - tb;
      const ca = new Date(a.createdAtDb || 0).getTime();
      const cb = new Date(b.createdAtDb || 0).getTime();
      return cb - ca;
    };

    if (showHistorico) {
      return base
        .filter(ev => historicoTypeMatch(ev, historicoTipo))
        .slice()
        .sort((a, b) => {
          const ta = comunidadEventDateSortKey(a);
          const tb = comunidadEventDateSortKey(b);
          const ka = ta === Infinity ? -Infinity : ta;
          const kb = tb === Infinity ? -Infinity : tb;
          return kb - ka;
        });
    }

    return base.filter(ev => eventMatchesChip(ev, activeChip)).slice().sort(byDestacaThenEventDate);
  }, [dbEvents, search, activeChip, showHistorico, historicoTipo, isAdmin, user?.id]);

  const hasFeedFilters = Boolean(
    search.trim() || (!showHistorico && activeChip !== 'Todo') || (showHistorico && historicoTipo !== 'Todo')
  );

  const abrirPublicar = async () => {
    setComunidadPublishBlockHint(null);
    if (!user?.id) {
      if (typeof onRequireAuth === 'function') onRequireAuth();
      return;
    }
    const gate = await resolveComunidadPublishGate(user);
    setIsAdmin(gate.isAdmin);
    setGroovesComunidadPhoneOk(!!gate.phoneOk);
    if (!gate.allowed) {
      setComunidadPublishBlockHint(gate.message);
      return;
    }
    if (gate.groovesBalance != null) setUserGroovesComunidad(gate.groovesBalance);
    setEditingId(null);
    setImagenError('');
    setFeedError(null);
    setForm(defaultForm());
    if (fileInputRef.current) fileInputRef.current.value = '';
    setPublicarOpen(true);
  };

  const abrirEditar = useCallback(
    async ev => {
      if (!user?.id || ev.authorId !== user.id) return;
      setComunidadPublishBlockHint(null);
      setImagenError('');
      setFeedError(null);
      const gate = await resolveComunidadPublishGate(user);
      setIsAdmin(gate.isAdmin);
      setGroovesComunidadPhoneOk(!!gate.phoneOk);
      if (!gate.allowed) {
        setComunidadPublishBlockHint(gate.message);
        return;
      }
      if (gate.groovesBalance != null) setUserGroovesComunidad(gate.groovesBalance);
      setEditingId(String(ev.id));
      setForm(eventToEditForm(ev));
      if (fileInputRef.current) fileInputRef.current.value = '';
      setPublicarOpen(true);
    },
    [user]
  );

  const onEliminarPublicacion = useCallback(
    async ev => {
      if (!user?.id || ev.authorId !== user.id) return;
      if (!window.confirm('¿Eliminar esta publicación? No se puede deshacer.')) return;
      setFeedError(null);
      const { error } = await deleteComunidadPublicacion(user.id, ev.id);
      if (error) {
        setFeedError(error.message);
        return;
      }
      setSavedItemIds(prev => {
        const n = new Set(prev);
        n.delete(String(ev.id));
        return n;
      });
      await reloadFeed();
    },
    [user, reloadFeed]
  );

  const onAdminToggleHidden = useCallback(
    async ev => {
      if (!user?.id || !isAdmin || !ev?._db) return;
      const next = !ev.isHidden;
      if (next && !window.confirm('¿Ocultar esta publicación a usuarios?')) return;
      setFeedError(null);
      const { error } = await setComunidadPublicacionHidden(ev.id, { hidden: next, reason: null });
      if (error) {
        setFeedError(error.message || 'No se pudo actualizar.');
        return;
      }
      await reloadFeed();
    },
    [user?.id, isAdmin, reloadFeed]
  );

  const onAdminEliminar = useCallback(
    async ev => {
      if (!user?.id || !isAdmin || !ev?._db) return;
      if (!window.confirm('¿Eliminar esta publicación? No se puede deshacer.')) return;
      setFeedError(null);
      const { error } = await adminDeleteComunidadPublicacion(ev.id);
      if (error) {
        setFeedError(error.message || 'No se pudo eliminar.');
        return;
      }
      await reloadFeed();
    },
    [user?.id, isAdmin, reloadFeed]
  );

  const revokeImagenBlobPreview = useCallback(() => {
    if (imagenBlobUrlRef.current) {
      URL.revokeObjectURL(imagenBlobUrlRef.current);
      imagenBlobUrlRef.current = null;
    }
  }, []);

  useEffect(() => () => revokeImagenBlobPreview(), [revokeImagenBlobPreview]);

  const cerrarPublicar = () => {
    setPublicarOpen(false);
    setEditingId(null);
    setImagenError('');
    setImagenPendingFile(null);
    setImagenPreparing(false);
    revokeImagenBlobPreview();
    setFeedError(null);
    setComunidadPublishBlockHint(null);
    setForm(defaultForm());
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onSelectImagen = async e => {
    setImagenError('');
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setImagenPendingFile(null);
      revokeImagenBlobPreview();
      setForm(f => ({ ...f, imagenPreview: null }));
      return;
    }
    if (!file.type.startsWith('image/')) {
      setImagenError('Elige un archivo de imagen (JPG, PNG, WebP o GIF).');
      return;
    }
    if (file.size > MAX_IMAGEN_BYTES) {
      setImagenError('La imagen no puede superar 3 MB. Prueba otra o comprímela.');
      return;
    }
    setImagenPreparing(true);
    try {
      const compressed = await compressImageForComunidad(file);
      if (compressed.size > MAX_IMAGEN_BYTES) {
        setImagenError('Tras optimizar sigue siendo demasiado grande. Usa otra imagen.');
        return;
      }
      revokeImagenBlobPreview();
      const blobUrl = URL.createObjectURL(compressed);
      imagenBlobUrlRef.current = blobUrl;
      setImagenPendingFile(compressed);
      setForm(f => ({ ...f, imagenPreview: blobUrl }));
    } catch (_) {
      setImagenError('No se pudo preparar la imagen.');
    } finally {
      setImagenPreparing(false);
    }
  };

  const quitarImagen = () => {
    setImagenError('');
    setImagenPendingFile(null);
    revokeImagenBlobPreview();
    setForm(f => ({ ...f, imagenPreview: null }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const locationIsValid = f => {
    if (!f.titulo.trim() || !f.ciudad.trim() || !f.via.trim()) return false;
    if (f.pais === 'ES' && !f.provincia) return false;
    if (f.pais && f.pais !== 'ES' && !f.provinciaTexto.trim()) return false;
    if (!f.pais) return false;
    return true;
  };

  const onSubmitPublicar = async e => {
    e.preventDefault();
    if (!user?.id) {
      if (typeof onRequireAuth === 'function') onRequireAuth();
      return;
    }
    const gate = await resolveComunidadPublishGate(user);
    setIsAdmin(gate.isAdmin);
    setGroovesComunidadPhoneOk(!!gate.phoneOk);
    if (!gate.allowed) {
      setFeedError(gate.message);
      return;
    }
    if (!locationIsValid(form)) return;
    const genreCheck = validateComunidadMusicGenres(form);
    if (!genreCheck.ok) {
      setFeedError(genreCheck.message);
      return;
    }
    const isEvento = form.tipo === TIPO_EVENTO;
    if (isEvento && form.entradaTipo === 'precio_fijo' && !form.entradaDetalle.trim()) return;
    if (isEvento && String(form.entradaEnlace || '').trim() && !isValidComunidadEntradaUrl(form.entradaEnlace)) {
      setFeedError('El enlace de entradas debe ser una URL segura (https://…).');
      return;
    }
    if (isEvento && String(form.entradaEnlace || '').trim() && !isAdmin) {
      const el = await fetchPartnerTicketSalesEligibility(user.id);
      if (!el.eligible) {
        setFeedError(el.reason || 'Solo partners verificados con suscripción pueden publicar enlaces de venta.');
        return;
      }
    }

    // Moderación + allowlist de enlaces (Comunidad)
    const mt = moderateText(form.titulo, 'comunidad_title');
    if (!mt.approved) {
      setFeedError(`No se pudo publicar · ${mt.reason || 'Revisa el texto.'}`);
      return;
    }
    const mb = moderateText(form.notas, 'comunidad_body');
    if (!mb.approved) {
      setFeedError(`No se pudo publicar · ${mb.reason || 'Revisa el texto.'}`);
      return;
    }

    setSubmitBusy(true);
    setFeedError(null);
    try {
      let formToSave = form;
      try {
        const imagenUrl = await resolveComunidadImagenUrlForSave({
          userId: user.id,
          publicationId: editingId,
          pendingFile: imagenPendingFile,
          previewUrl: form.imagenPreview,
        });
        formToSave = { ...form, imagenPreview: imagenUrl };
      } catch (imgErr) {
        setFeedError(String(imgErr?.message || imgErr || 'No se pudo subir la imagen.'));
        return;
      }

      let savedId = editingId;
      if (editingId) {
        const { error } = await updateComunidadPublicacion(user.id, editingId, formToSave);
        if (error) {
          setFeedError(mapComunidadSaveError(error));
          return;
        }
      } else {
        const { data: inserted, error } = await insertComunidadPublicacion(user.id, formToSave);
        if (error) {
          setFeedError(mapComunidadSaveError(error));
          return;
        }
        savedId = inserted?.id ?? null;
        if (savedId && isEvento) {
          triggerComunidadDuplicateCheckAfterPublish(supabase, savedId);
        }
      }

      let entradaVerifyNote = '';
      if (savedId && isEvento && String(formToSave.entradaEnlace || '').trim()) {
        const vr = await tryAutoVerifyEntradaAfterSave(savedId, formToSave);
        if (vr.ok && vr.verified) {
          entradaVerifyNote =
            ' El enlace de entradas ha pasado la verificación automática y el botón «Comprar entradas» será visible cuando la publicación esté aprobada en el feed.';
        } else if (vr.ok && !vr.skipped) {
          entradaVerifyNote =
            ' El enlace de entradas queda pendiente de revisión: un administrador lo validará antes de mostrar el botón de compra.';
        }
      }

      setImagenPendingFile(null);
      revokeImagenBlobPreview();
      await reloadFeed();
      cerrarPublicar();
      if (!editingId) {
        if (publishNoticeTimerRef.current) clearTimeout(publishNoticeTimerRef.current);
        setPublishNotice(
          `Tu publicación se ha enviado correctamente. Aparecerá en el feed público cuando un administrador la apruebe; mientras tanto solo tú la ves aquí marcada como «en revisión».${entradaVerifyNote}`
        );
        publishNoticeTimerRef.current = window.setTimeout(() => {
          setPublishNotice(null);
          publishNoticeTimerRef.current = null;
        }, 18000);
      } else if (entradaVerifyNote) {
        if (publishNoticeTimerRef.current) clearTimeout(publishNoticeTimerRef.current);
        setPublishNotice(`Cambios guardados.${entradaVerifyNote}`);
        publishNoticeTimerRef.current = window.setTimeout(() => {
          setPublishNotice(null);
          publishNoticeTimerRef.current = null;
        }, 14000);
      }
    } finally {
      setSubmitBusy(false);
    }
  };

  return (
    <div style={{ background:C.dark, minHeight:'100vh', color:'#fff' }}>
      <WebTopBar
        navActive="comunidad"
        dark
        user={user}
        onNavigate={onNavigate}
        onShowModal={tab => onRequireAuth?.(typeof tab === 'string' ? tab : 'login')}
        onSignOut={onBack}
      />

      {publicarOpen && (
        <>
          <div
            onClick={cerrarPublicar}
            style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:400 }}
            role="presentation"
            aria-hidden
          />
          <div
            style={{
              position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', zIndex:401,
              width:'min(540px, 96vw)', maxHeight:'min(92vh, 720px)', overflowY:'auto',
              background:'linear-gradient(180deg, #1a1d28 0%, #12141c 100%)', borderRadius:16,
              border:'1px solid rgba(200,168,75,0.25)', boxShadow:'0 24px 64px rgba(0,0,0,0.5)', padding:'22px 22px 18px',
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="comunidad-publicar-title"
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
              <div>
                <h2 id="comunidad-publicar-title" style={{ fontSize:17, fontWeight:800, color:'#fff', margin:0, letterSpacing:'-0.02em' }}>
                  {editingId ? 'Editar publicación' : 'Publicar a la comunidad'}
                </h2>
                <p style={{ fontSize:12, color:'rgba(255,255,255,0.45)', margin:'6px 0 0', lineHeight:1.45 }}>
                  Evento, tienda, pub, mercadillo u otro lugar con música en vivo o vinilos. Tras enviar,{' '}
                  <strong style={{ color: 'rgba(255,255,255,0.65)' }}>un administrador la revisará</strong> antes de mostrarla a todos los visitantes.
                </p>
              </div>
              <button
                type="button"
                onClick={cerrarPublicar}
                style={{ background:'none', border:'none', color:'rgba(255,255,255,0.4)', fontSize:22, lineHeight:1, cursor:'pointer', padding:4 }}
                aria-label="Cerrar"
              >×
              </button>
            </div>

            <form onSubmit={onSubmitPublicar} style={{ display:'flex', flexDirection:'column', gap:12 }}>
              {feedError ? (
                <div style={{ fontSize: 12, color: 'rgba(255,180,180,0.95)', lineHeight: 1.45, padding: '8px 10px', borderRadius: 8, background: 'rgba(226,75,74,0.12)', border: '0.5px solid rgba(226,75,74,0.35)' }}>
                  <div>{feedError}</div>
                  {feedError.includes('Ajustes → Cuenta') ? (
                    <button
                      type="button"
                      onClick={() => {
                        if (typeof onNavigate === 'function') onNavigate('ajustes', { initialSub: 'cuenta' });
                      }}
                      style={{
                        marginTop: 10,
                        padding: '6px 12px',
                        borderRadius: 8,
                        border: `0.5px solid ${C.gold}`,
                        background: 'rgba(200,168,75,0.14)',
                        color: C.gold,
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}>
                      Ir a Ajustes → Cuenta
                    </button>
                  ) : null}
                </div>
              ) : null}
              <label style={{ fontSize:11, color:'rgba(255,255,255,0.5)', textTransform:'uppercase', letterSpacing:0.6 }}>Título *</label>
              <input
                required
                value={form.titulo}
                onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
                placeholder="Ej. Jam session jueves en Bar Central"
                style={inputBase}
                autoFocus
              />

              <label style={{ fontSize:11, color:'rgba(255,255,255,0.5)', textTransform:'uppercase', letterSpacing:0.6 }}>Qué publicas *</label>
              <select
                value={form.tipo}
                onChange={e => {
                  const t = e.target.value;
                  setForm(f => ({
                    ...f,
                    tipo: t,
                    ...(t !== TIPO_EVENTO
                      ? { entradaTipo: 'libre', entradaDetalle: '', entradaEnlace: '' }
                      : {}),
                  }));
                }}
                style={{ ...inputBase, cursor:'pointer' }}
              >
                {COMUNIDAD_TIPOS_PUBLICACION.map(({ value, label }) => (
                  <option key={value} value={value} style={{ background:'#1a1d28', color:'#fff' }}>
                    {label}
                  </option>
                ))}
              </select>

              <ComunidadMusicGenresField
                mode={form.musicGenreMode || 'specific'}
                genres={form.musicGenres || []}
                onChange={patch => setForm(f => ({ ...f, ...patch }))}
              />

              <label style={labelForm}>Foto, cartel o imagen (opcional)</label>
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', margin: '0 0 8px', lineHeight: 1.4 }}>
                Cartel o foto del local. JPG, PNG, WebP o GIF · máx. 3 MB. Se optimiza y aloja en DreamVinyl al publicar.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept={IMAGEN_ACCEPT}
                onChange={onSelectImagen}
                style={{ display: 'none' }}
                id="comunidad-imagen-input"
              />
              <div
                style={{
                  border: '1px dashed rgba(200,168,75,0.35)', borderRadius: 12, padding: 12,
                  background: 'rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start',
                }}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  <label
                    htmlFor="comunidad-imagen-input"
                    style={{
                      display: 'inline-block', padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                      background: `linear-gradient(180deg, ${C.gold} 0%, #b8943a 100%)`, color: '#0a0a0f', fontWeight: 700, fontSize: 12, border: 'none',
                    }}
                  >{imagenPreparing ? 'Optimizando…' : '+ Añadir imagen'}
                  </label>
                  {imagenPreparing ? (
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>Preparando vista previa…</span>
                  ) : null}
                  {form.imagenPreview ? (
                    <button type="button" onClick={quitarImagen} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(226,75,74,0.4)', background: 'transparent', color: 'rgba(255,180,180,0.95)', fontSize: 12, cursor: 'pointer' }}>Quitar</button>
                  ) : null}
                </div>
                {imagenError ? <div style={{ color: 'rgba(226,120,100,0.95)', fontSize: 12 }}>{imagenError}</div> : null}
                {form.imagenPreview ? (
                  <div
                    style={{
                      width: '100%',
                      aspectRatio: '16 / 9',
                      minHeight: 120,
                      maxHeight: 200,
                      borderRadius: 8,
                      overflow: 'hidden',
                      border: '1px solid rgba(255,255,255,0.12)',
                      position: 'relative',
                      background: 'rgba(0,0,0,0.35)',
                      lineHeight: 0,
                    }}
                  >
                    <img
                      src={form.imagenPreview}
                      alt="Vista previa del cartel o foto"
                      style={comunidadFeedMediaImg}
                    />
                  </div>
                ) : null}
              </div>

              <div style={{ fontSize:11, color:'rgba(255,255,255,0.55)', fontWeight:800, textTransform:'uppercase', letterSpacing:0.6, marginTop:2 }}>Ubicación *</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, alignItems:'end' }}>
                <div>
                  <label style={labelForm}>País *</label>
                  <select
                    value={form.pais}
                    onChange={e =>
                      setForm(f => ({
                        ...f,
                        pais: e.target.value,
                        provincia: '',
                        provinciaTexto: '',
                        ciudad: '',
                      }))
                    }
                    required
                    style={{ ...inputBase, marginTop:6, cursor:'pointer' }}
                  >
                    {PAISES_COMUN.map(({ v, l }) => (
                      <option key={v} value={v} style={{ background:'#1a1d28', color:'#fff' }}>{l}</option>
                    ))}
                  </select>
                </div>
                {form.pais === 'ES' ? (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <UbicacionSelector
                      variant="dark"
                      required
                      soloEspana
                      provincia={form.provincia}
                      municipio={form.ciudad}
                      onProvinciaChange={n => setForm(f => ({ ...f, provincia: n, ciudad: '' }))}
                      onMunicipioChange={n => setForm(f => ({ ...f, ciudad: n }))}
                    />
                  </div>
                ) : (
                  <div>
                    <label style={labelForm}>Provincia o región *</label>
                    <input
                      value={form.provinciaTexto}
                      onChange={e => setForm(f => ({ ...f, provinciaTexto: e.target.value }))}
                      required
                      placeholder="Ej. Île-de-France, distretto…"
                      style={{ ...inputBase, marginTop:6 }}
                    />
                  </div>
                )}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={labelForm}>Ciudad o municipio *</label>
                  {form.pais === 'ES' ? (
                    <div style={{ marginTop: 6, fontSize: 11, color: 'rgba(255,255,255,0.45)', lineHeight: 1.35 }}>
                      En España, el municipio se elige arriba en el selector.
                    </div>
                  ) : (
                    <input
                      value={form.ciudad}
                      onChange={e => setForm(f => ({ ...f, ciudad: e.target.value }))}
                      required
                      placeholder="Ej. Madrid, Barcelona…"
                      style={{ ...inputBase, marginTop:6 }}
                    />
                  )}
                </div>
                <div>
                  <label style={labelForm}>C.P. (opcional)</label>
                  <input
                    value={form.cp}
                    onChange={e => {
                      const v = e.target.value;
                      setForm(f => ({
                        ...f,
                        cp: f.pais === 'ES' ? v.replace(/\D/g, '').slice(0, 5) : v.replace(/[<>"]/g, '').slice(0, 10),
                      }));
                    }}
                    inputMode={form.pais === 'ES' ? 'numeric' : 'text'}
                    placeholder={form.pais === 'ES' ? 'Ej. 28013' : 'Ej. 75001'}
                    style={{ ...inputBase, marginTop:6 }}
                  />
                </div>
              </div>
              <div>
                <label style={labelForm}>Calle, número, piso, portal… *</label>
                <input
                  value={form.via}
                  onChange={e => setForm(f => ({ ...f, via: e.target.value }))}
                  required
                  placeholder="Ej. C/ Mayor, 12 — 1º A"
                  style={{ ...inputBase, marginTop:6 }}
                />
              </div>

              <div
                style={{
                  borderRadius:12, padding:12, marginTop:4,
                  background:'rgba(200,168,75,0.08)', border:'1px dashed rgba(200,168,75,0.3)',
                }}
              >
                <p style={{ fontSize:11, color:'rgba(255,255,255,0.42)', margin:0, marginBottom:8, lineHeight:1.45, fontStyle:'italic' }}>
                  Pega aquí el enlace de <strong style={{ color:'rgba(200,168,75,0.9)', fontStyle:'normal' }}>Google Maps</strong> si lo tienes a mano: copia y pega el enlace desde «Compartir» en el mapa o en la ficha del sitio. Es opcional, pero ayuda a encontrar el sitio.
                </p>
                <input
                  value={form.mapsUrl}
                  onChange={e => setForm(f => ({ ...f, mapsUrl: e.target.value }))}
                  autoComplete="off"
                  placeholder="https://maps.app.goo.gl/… o un enlace corto de compartir"
                  style={{ ...inputBase, background:'rgba(0,0,0,0.2)', fontSize:12 }}
                />
              </div>

              <div>
                <label style={labelForm}>Fecha u horario</label>
                <input
                  value={form.fecha}
                  onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))}
                  placeholder="ej. 12 Jul · 21:00h"
                  style={{ ...inputBase, marginTop:6 }}
                />
              </div>

              {form.tipo === TIPO_EVENTO && (
                <>
                  <label style={labelForm}>TIPO DE ENTRADA *</label>
                  <select
                    value={form.entradaTipo}
                    onChange={e => setForm(f => ({ ...f, entradaTipo: e.target.value, entradaDetalle: '' }))}
                    style={selEntrada}
                  >
                    {ENTRADA_TIPO_OPCIONES.map(({ v, l }) => (
                      <option key={v} value={v} style={{ background: '#1a1d28', color: '#e8edf2' }}>{l}</option>
                    ))}
                  </select>

                  {form.entradaTipo === 'precio_fijo' && (
                    <div style={{ width: '100%' }}>
                      <label style={labelForm}>PRECIO *</label>
                      <input
                        value={form.entradaDetalle}
                        onChange={e => setForm(f => ({ ...f, entradaDetalle: e.target.value }))}
                        required
                        placeholder="Ej: 5€, 8€, 10€…"
                        style={{ ...subInpEntrada, width: '50%', marginTop: 4 }}
                      />
                    </div>
                  )}
                  {form.entradaTipo === 'consumicion' && (
                    <div>
                      <label style={labelForm}>PRECIO CON CONSUMICIÓN (OPCIONAL)</label>
                      <input
                        value={form.entradaDetalle}
                        onChange={e => setForm(f => ({ ...f, entradaDetalle: e.target.value }))}
                        placeholder="Ej: 5€ con una copa incluida"
                        style={{ ...subInpEntrada, width: '100%', marginTop: 4 }}
                      />
                    </div>
                  )}
                  {form.entradaTipo === 'otros' && (
                    <div>
                      <label style={labelForm}>ESPECIFICA</label>
                      <input
                        value={form.entradaDetalle}
                        onChange={e => setForm(f => ({ ...f, entradaDetalle: e.target.value }))}
                        placeholder="Ej: Reserva de mesa, bono de 3 copas…"
                        style={{ ...subInpEntrada, width: '100%', marginTop: 4 }}
                      />
                    </div>
                  )}

                  <div style={{ height: 1, background: '#1e2d3d', width: '100%', margin: '4px 0' }} role="separator" />
                  <label style={labelForm} id="comunidad-entrada-enlace-label">
                    🎟 Enlace de venta de entradas (opcional)
                    {!(ticketSalesEligibility.eligible || isAdmin) ? (
                      <span style={{ fontWeight: 500, color: 'rgba(255,255,255,0.38)', textTransform: 'none', letterSpacing: 0 }}>
                        {' '}
                        — solo partners con suscripción
                      </span>
                    ) : null}
                  </label>
                  {ticketSalesEligibility.eligible || isAdmin ? (
                    <>
                      <p style={{ fontSize: 10, lineHeight: 1.5, color: '#8899aa', margin: '4px 0 8px' }}>
                        Enlace oficial de tu ticketera o web de venta. Tras publicar, DreamVinyl verificará la URL; el botón «Comprar entradas» solo aparece si la verificación es correcta.
                      </p>
                      <input
                        value={form.entradaEnlace}
                        onChange={e => setForm(f => ({ ...f, entradaEnlace: e.target.value }))}
                        placeholder="https://tu-ticketera.com/evento…"
                        style={subInpEnlaceVenta}
                        aria-labelledby="comunidad-entrada-enlace-label"
                      />
                    </>
                  ) : (
                    <fieldset
                      disabled
                      aria-labelledby="comunidad-entrada-enlace-label"
                      style={{
                        margin: 0,
                        padding: 0,
                        border: 'none',
                        minWidth: 0,
                        opacity: 1,
                      }}
                    >
                      <div
                        role="alert"
                        style={{
                          margin: '6px 0 10px',
                          padding: '11px 12px',
                          borderRadius: 10,
                          background: 'rgba(200,168,75,0.1)',
                          border: '0.5px solid rgba(200,168,75,0.38)',
                        }}
                      >
                        <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: C.gold, lineHeight: 1.45 }}>
                          Solo disponible para partners verificados con suscripción activa
                        </p>
                        <p style={{ margin: '7px 0 0', fontSize: 10, color: 'rgba(255,255,255,0.52)', lineHeight: 1.5 }}>
                          {ticketSalesEligibility.loading
                            ? 'Comprobando tu cuenta partner…'
                            : ticketSalesEligibility.reason ||
                              'Los usuarios de la comunidad pueden publicar el evento, pero el enlace de venta de entradas lo gestionan promotores y salas con partner aprobado y suscripción de venta activa.'}
                        </p>
                      </div>
                      <div
                        aria-hidden
                        style={{
                          ...subInpEnlaceVenta,
                          marginTop: 0,
                          display: 'flex',
                          alignItems: 'center',
                          minHeight: 40,
                          color: 'rgba(255,255,255,0.28)',
                          fontSize: 12,
                          cursor: 'default',
                          userSelect: 'none',
                          pointerEvents: 'none',
                          background: 'rgba(255,255,255,0.03)',
                          border: '0.5px solid rgba(255,255,255,0.08)',
                        }}
                      >
                        No disponible para tu cuenta
                      </div>
                    </fieldset>
                  )}
                </>
              )}

              <label style={{ fontSize:11, color:'rgba(255,255,255,0.5)', textTransform:'uppercase', letterSpacing:0.6 }}>Detalles (opcional)</label>
              <textarea
                value={form.notas}
                onChange={e => setForm(f => ({ ...f, notas: e.target.value }))}
                placeholder="Web, teléfono, estilos de música, edad mínima…"
                rows={3}
                style={{ ...inputBase, resize:'vertical', minHeight:72 }}
              />

              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:8, paddingTop:4 }}>
                <button type="button" onClick={cerrarPublicar} style={btnSecondary} disabled={submitBusy}>
                  Cancelar
                </button>
                <button type="submit" style={{ ...btnPrimary, opacity: submitBusy ? 0.65 : 1 }} disabled={submitBusy}>
                  {submitBusy ? 'Guardando…' : editingId ? 'Guardar cambios' : 'Publicar'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      <div style={{ maxWidth:1400, margin:'0 auto', padding:'20px 20px 60px' }}>
        <div
          style={{
            display:'grid',
            gridTemplateColumns: (isMobile || isTablet) ? 'minmax(0,1fr)' : '220px minmax(0,1fr) 200px',
            gap: isMobile ? 12 : 18,
            alignItems:'start',
          }}
        >
          {!(isMobile || isTablet) ? (
            <aside style={{ position:'sticky', top:70, alignSelf:'start', display:'flex', flexDirection:'column', gap:14 }}>
            <DCard style={{ padding:16, borderColor:'rgba(200,168,75,0.28)', display:'flex', flexDirection:'column', alignItems:'stretch', gap:0 }}>
              <button type="button" onClick={abrirPublicar} style={{ width:'100%', padding:'14px 14px', borderRadius:12, background:`linear-gradient(180deg, ${C.gold} 0%, #b8943a 100%)`, color:'#0a0a0f', border:'none', fontSize:13, fontWeight:900, cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:6, boxShadow:'0 4px 18px rgba(200,168,75,0.35)', letterSpacing:'0.02em' }}>
                <span style={{ width:40, height:40, borderRadius:'50%', background:'rgba(0,0,0,0.12)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <Icon name="plus" size={20} color="#0a0a0f"/>
                </span>
                Publicar
              </button>
              <p style={{ fontSize:11, color:'rgba(255,255,255,0.48)', lineHeight:1.5, margin:0, marginTop:14, textAlign:'center' }}>
                Comparte con la comunidad lugares con encanto musical, eventos, pub´s, discotecas, musica en vivo, tiendas, lugares, mercados y cualquier cosa que ayude a conectar a quienes viven y disfrutan de la musica.
              </p>
              {user?.id && !isAdmin && !sorteosLoading ? (
                <div
                  style={{
                    marginTop: 12,
                    fontSize: 10,
                    lineHeight: 1.65,
                    color: 'rgba(255,255,255,0.52)',
                    textAlign: 'left',
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                    paddingTop: 10,
                  }}>
                  <div style={{ fontWeight: 800, marginBottom: 6, color: 'rgba(255,255,255,0.42)', letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: 9 }}>
                    Requisito para publicar
                  </div>
                  <div>
                    Correo:{' '}
                    {user.email_confirmed_at ? (
                      <span style={{ color: C.green }}>confirmado</span>
                    ) : (
                      <span style={{ color: 'rgba(255,170,130,0.95)' }}>pendiente</span>
                    )}
                  </div>
                  <div>
                    Teléfono (SMS):{' '}
                    {groovesComunidadPhoneOk ? (
                      <span style={{ color: C.green }}>verificado</span>
                    ) : (
                      <span style={{ color: 'rgba(255,170,130,0.95)' }}>pendiente</span>
                    )}
                  </div>
                  {(!user.email_confirmed_at || !groovesComunidadPhoneOk) && typeof onNavigate === 'function' ? (
                    <button
                      type="button"
                      onClick={() => onNavigate('ajustes', { initialSub: 'cuenta' })}
                      style={{
                        marginTop: 8,
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: 8,
                        border: `0.5px solid ${C.gold}`,
                        background: 'rgba(200,168,75,0.12)',
                        color: C.gold,
                        fontSize: 10,
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}>
                      Ir a Ajustes → Cuenta
                    </button>
                  ) : null}
                </div>
              ) : null}
              {comunidadPublishBlockHint ? (
                <div
                  role="alert"
                  style={{
                    marginTop: 10,
                    fontSize: 10,
                    lineHeight: 1.5,
                    color: 'rgba(255,200,200,0.95)',
                    padding: '8px 10px',
                    borderRadius: 8,
                    background: 'rgba(226,75,74,0.12)',
                    border: '0.5px solid rgba(226,75,74,0.35)',
                  }}>
                  {comunidadPublishBlockHint}
                </div>
              ) : null}
            </DCard>
            <TopGroovesRanking
              onNavigate={onNavigate}
              onViewFullRanking={() => onNavigate('grooves-ranking')}
              limit={10}
            />
            </aside>
          ) : null}

          <main style={{ minWidth:0 }}>
            {publishNotice ? (
              <div
                role="status"
                style={{
                  marginBottom: 14,
                  padding: '11px 14px',
                  borderRadius: 12,
                  background: 'rgba(29,158,117,0.14)',
                  border: '0.5px solid rgba(29,158,117,0.35)',
                  color: C.green,
                  fontSize: 12,
                  lineHeight: 1.45,
                }}
              >
                {publishNotice}
              </div>
            ) : null}
            <div style={{ textAlign: 'center', marginBottom: 20, paddingTop: 2 }}>
              <p style={{ margin: 0, marginBottom: 6, fontSize: 9, fontWeight: 800, letterSpacing: '0.2em', color: C.gold, textTransform: 'uppercase' }}>Comunidad</p>
              <h1
                style={{
                  margin: 0,
                  fontSize: 'clamp(1.15rem, 2.4vw, 1.5rem)',
                  fontWeight: 800,
                  color: '#f4f2ec',
                  letterSpacing: '-0.03em',
                  lineHeight: 1.15,
                }}
              >
                Eventos <span style={{ color: 'rgba(200,168,75,0.5)', fontWeight: 500, margin: '0 0.15em' }}>·</span> Ocio
              </h1>
              <div
                aria-hidden
                style={{
                  width: 56, height: 2, borderRadius: 1, margin: '12px auto 0',
                  background: `linear-gradient(90deg, transparent, ${C.gold}, transparent)`,
                  boxShadow: `0 0 20px ${C.gold}33`,
                }}
              />
            </div>
            <div
              role="tablist"
              aria-label="Secciones comunidad"
              style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}
            >
              {[
                { id: 'feed', label: 'Eventos y ocio' },
                { id: 'lugares', label: 'Descubre lugares' },
              ].map(t => {
                const active =
                  (t.id === 'feed' && comunidadTab === 'feed' && !lugarFicha) ||
                  (t.id === 'lugares' && comunidadTab === 'lugares');
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => {
                      onCambiarComunidadTab(t.id);
                      setLugarFicha(null);
                    }}
                    style={{
                      padding: '8px 18px',
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer',
                      border: active ? `0.5px solid ${C.gold}` : '0.5px solid rgba(255,255,255,0.2)',
                      color: active ? C.gold : 'rgba(255,255,255,0.55)',
                      background: active ? 'rgba(200,168,75,0.14)' : 'rgba(0,0,0,0.25)',
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            {lugarFicha ? (
              <section aria-label="Ficha de lugar verificado">
                <DCard style={{ padding: 18, marginBottom: 18, borderColor: 'rgba(200,168,75,0.28)' }}>
                  <button
                    type="button"
                    onClick={() => setLugarFicha(null)}
                    style={{
                      background: 'transparent',
                      border: '0.5px solid rgba(255,255,255,0.2)',
                      color: 'rgba(255,255,255,0.75)',
                      borderRadius: 10,
                      padding: '8px 14px',
                      fontSize: 12,
                      cursor: 'pointer',
                      marginBottom: 14,
                    }}
                  >
                    ← Volver a lugares
                  </button>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-start' }}>
                    <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                      <h2 style={{ margin: '0 0 8px', fontSize: 'clamp(1.1rem, 2.2vw, 1.35rem)', color: '#fff', fontWeight: 800 }}>
                        {lugarFicha.company_name || 'Lugar'}
                      </h2>
                      <p style={{ margin: '0 0 10px', fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                        {[lugarFicha.city, lugarFicha.country].filter(Boolean).join(' · ')}
                        {lugarFicha.sector ? ` · ${lugarFicha.sector}` : ''}
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                        {(lugarFicha.place_roles || []).map(rk => (
                          <span
                            key={rk}
                            style={{
                              fontSize: 10,
                              padding: '3px 9px',
                              borderRadius: 8,
                              background: 'rgba(200,168,75,0.12)',
                              border: `0.5px solid ${C.gold}`,
                              color: C.gold,
                              fontWeight: 700,
                            }}
                          >
                            {PARTNER_ROLE_LABELS[rk] || rk}
                          </span>
                        ))}
                        {lugarFicha.featured_in_directory ? (
                          <span
                            style={{
                              fontSize: 10,
                              padding: '3px 9px',
                              borderRadius: 8,
                              background: 'rgba(29,158,117,0.12)',
                              border: '0.5px solid rgba(29,158,117,0.35)',
                              color: C.green,
                              fontWeight: 700,
                            }}
                          >
                            Destacado
                          </span>
                        ) : null}
                      </div>
                      {lugarFicha.description ? (
                        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.72)', lineHeight: 1.55, margin: '0 0 12px' }}>{lugarFicha.description}</p>
                      ) : null}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                        {lugarFicha.website_url ? (
                          <a
                            href={hrefMaps(lugarFicha.website_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 12, color: C.gold, fontWeight: 600 }}
                          >
                            Web →
                          </a>
                        ) : null}
                        {lugarFicha.maps_url ? (
                          <a
                            href={hrefMaps(lugarFicha.maps_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 12, color: C.gold, fontWeight: 600 }}
                          >
                            Google Maps →
                          </a>
                        ) : null}
                        {lugarFicha.lat != null && lugarFicha.lng != null ? (
                          <a
                            href={`https://www.google.com/maps?q=${encodeURIComponent(`${lugarFicha.lat},${lugarFicha.lng}`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 12, color: C.gold, fontWeight: 600 }}
                          >
                            Mapa →
                          </a>
                        ) : null}
                        {lugarFicha.public_username ? (
                          <button
                            type="button"
                            onClick={() =>
                              onNavigate('perfil-vendedor', {
                                seller: lugarFicha.public_username,
                                userId: lugarFicha.profile_id,
                                returnTo: 'comunidad',
                              })
                            }
                            style={{
                              background: 'rgba(255,255,255,0.06)',
                              border: '0.5px solid rgba(255,255,255,0.15)',
                              color: '#fff',
                              borderRadius: 10,
                              padding: '6px 12px',
                              fontSize: 12,
                              cursor: 'pointer',
                              fontWeight: 600,
                            }}
                          >
                            Perfil @{lugarFicha.public_username}
                          </button>
                        ) : null}
                      </div>
                      <h3 style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', color: C.gold, textTransform: 'uppercase', margin: '16px 0 8px' }}>
                        Horarios por rol
                      </h3>
                      <ul style={{ margin: 0, paddingLeft: 18, color: 'rgba(255,255,255,0.65)', fontSize: 12, lineHeight: 1.6 }}>
                        {(lugarFicha.place_roles || []).map(rk => {
                          const sch = lugarFicha.schedules_by_role && typeof lugarFicha.schedules_by_role === 'object' ? lugarFicha.schedules_by_role[rk] : null;
                          const st = sch && typeof sch.schedule_type === 'string' ? sch.schedule_type : 'standard';
                          return (
                            <li key={rk}>
                              <strong style={{ color: '#fff' }}>{PARTNER_ROLE_LABELS[rk] || rk}:</strong> {scheduleTypeTitle(st)}
                            </li>
                          );
                        })}
                      </ul>
                      <h3 style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', color: C.gold, textTransform: 'uppercase', margin: '16px 0 8px' }}>
                        Géneros musicales
                      </h3>
                      {Array.isArray(lugarFicha.music_genres) && lugarFicha.music_genres.length ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {lugarFicha.music_genres.map(g => (
                            <span
                              key={g}
                              style={{
                                fontSize: 11,
                                padding: '4px 10px',
                                borderRadius: 999,
                                background: 'rgba(255,255,255,0.06)',
                                border: '0.5px solid rgba(255,255,255,0.12)',
                                color: 'rgba(255,255,255,0.85)',
                              }}
                            >
                              {g}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', margin: 0 }}>Sin géneros indicados aún (el partner puede añadirlos en su panel).</p>
                      )}
                    </div>
                    <div style={{ flex: '1 1 280px', minWidth: 0, maxWidth: 480 }}>
                      {(() => {
                        const pics = mergePhotoGallery(lugarFicha.logo_url, lugarFicha.photos_urls);
                        if (!pics.length) {
                          return (
                            <div
                              style={{
                                height: 200,
                                borderRadius: 12,
                                background: 'linear-gradient(135deg,#1a0d2e,#0d1a2e)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'rgba(255,255,255,0.35)',
                                fontSize: 12,
                              }}
                            >
                              Sin fotos públicas
                            </div>
                          );
                        }
                        const main = pics[Math.min(lugarGalleryIdx, pics.length - 1)];
                        return (
                          <div>
                            <div
                              style={{
                                height: 220,
                                borderRadius: 12,
                                overflow: 'hidden',
                                marginBottom: 8,
                                background: `#0b0f14 url(${main}) center/cover`,
                              }}
                            />
                            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
                              {pics.map((u, i) => (
                                <button
                                  key={u}
                                  type="button"
                                  onClick={() => setLugarGalleryIdx(i)}
                                  style={{
                                    width: 56,
                                    height: 56,
                                    flexShrink: 0,
                                    borderRadius: 8,
                                    border: i === lugarGalleryIdx ? `2px solid ${C.gold}` : '1px solid rgba(255,255,255,0.15)',
                                    padding: 0,
                                    cursor: 'pointer',
                                    background: `#111 url(${u}) center/cover`,
                                  }}
                                  aria-label={`Foto ${i + 1}`}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <h3 style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', color: C.gold, textTransform: 'uppercase', margin: '20px 0 10px' }}>
                    Valoraciones (surcos)
                  </h3>
                  <DCard style={{ padding: 14, marginBottom: 14, background: 'rgba(0,0,0,0.2)' }}>
                    {lugarReviewsLoading ? (
                      <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', margin: 0 }}>Cargando valoraciones…</p>
                    ) : (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
                          <div>
                            <div style={{ fontSize: 28, fontWeight: 600, color: C.gold, lineHeight: 1 }}>
                              {lugarRatingStats.count === 0 ? '—' : lugarRatingStats.avg}
                            </div>
                            <div style={{ margin: '6px 0 4px' }}>
                              <SurcosRating value={lugarRatingStats.surcos} readOnly size="sm" />
                            </div>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                              {lugarRatingStats.count} valoracion{lugarRatingStats.count === 1 ? '' : 'es'}
                            </div>
                          </div>
                          <div style={{ flex: 1 }}>
                            {lugarRatingStats.histogram.map(({ star, pct }) => (
                              <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                                <span style={{ fontSize: 8, color: 'rgba(255,255,255,0.4)', width: 8 }}>{star}</span>
                                <div style={{ flex: 1, height: 3, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${pct}%`, background: C.gold, borderRadius: 2 }} />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        {lugarRatingStats.topTags.length ? (
                          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 10 }}>
                            {lugarRatingStats.topTags.map(tg => (
                              <span
                                key={tg}
                                style={{
                                  fontSize: 9,
                                  padding: '2px 8px',
                                  borderRadius: 8,
                                  background: 'rgba(29,158,117,0.1)',
                                  color: C.green,
                                  border: '0.5px solid rgba(29,158,117,0.2)',
                                }}
                              >
                                {tg}
                              </span>
                            ))}
                          </div>
                        ) : null}
                        {lugarReviews.slice(0, 12).map(r => {
                          const rev = embedReviewerReview(r);
                          const uname = rev.username ?? 'Usuario';
                          const sur = Math.min(5, Math.max(1, Math.round(Number(r.rating) || 1)));
                          const tgs = Array.isArray(r.tags) ? r.tags : [];
                          return (
                            <div
                              key={r.id}
                              style={{
                                marginTop: 10,
                                padding: 10,
                                borderRadius: 10,
                                background: 'rgba(255,255,255,0.04)',
                                border: '0.5px solid rgba(255,255,255,0.08)',
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                                <span style={{ fontSize: 11, fontWeight: 600, color: '#fff' }}>@{uname}</span>
                                <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)' }}>
                                  {r.created_at ? new Date(r.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                                </span>
                              </div>
                              <SurcosRating value={sur} readOnly size="sm" />
                              {tgs.length ? (
                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                                  {tgs.map(tgx => (
                                    <span key={String(tgx)} style={{ fontSize: 8, padding: '2px 6px', borderRadius: 6, background: 'rgba(29,158,117,0.12)', color: C.green }}>
                                      {tgx}
                                    </span>
                                  ))}
                                </div>
                              ) : null}
                              {r.comment?.trim() ? (
                                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.45, marginTop: 6 }}>{r.comment.trim()}</div>
                              ) : null}
                              {r.seller_public_reply?.trim() ? (
                                <div
                                  style={{
                                    marginTop: 8,
                                    padding: '8px 10px',
                                    borderRadius: 8,
                                    background: 'rgba(200,168,75,0.08)',
                                    border: '0.5px solid rgba(200,168,75,0.22)',
                                    fontSize: 11,
                                    color: 'rgba(255,255,255,0.75)',
                                    lineHeight: 1.45,
                                  }}
                                >
                                  <strong style={{ color: C.gold }}>Respuesta pública:</strong> {r.seller_public_reply.trim()}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </DCard>
                  <h3 style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', color: C.gold, textTransform: 'uppercase', margin: '18px 0 10px' }}>
                    Comunidad
                  </h3>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', margin: '0 0 10px', lineHeight: 1.5 }}>
                    Publicaciones aprobadas del mismo perfil en esta sección. Pulsa para ir al listado principal.
                  </p>
                  {publicacionesComunidadDelLugar.length === 0 ? (
                    <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', margin: 0 }}>Aún no hay publicaciones visibles en Comunidad.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {publicacionesComunidadDelLugar.slice(0, 8).map(ev => (
                        <button
                          key={ev.id}
                          type="button"
                          onClick={() => verPublicacionEnFeed(ev.id)}
                          style={{
                            textAlign: 'left',
                            padding: '10px 12px',
                            borderRadius: 10,
                            border: '0.5px solid rgba(255,255,255,0.1)',
                            background: 'rgba(255,255,255,0.04)',
                            color: '#fff',
                            cursor: 'pointer',
                          }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{ev.title}</div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>
                            {[ev.date, ev.place].filter(Boolean).join(' · ')}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </DCard>
              </section>
            ) : comunidadTab === 'lugares' ? (
              <section aria-label="Directorio de lugares verificados">
                {lugaresErr ? (
                  <div style={{ padding: 12, borderRadius: 10, background: 'rgba(226,75,74,0.12)', color: '#ffb4b4', fontSize: 12, marginBottom: 12 }}>{lugaresErr}</div>
                ) : null}
                <ComunidadSearchInput
                  value={lugaresSearch}
                  onChange={e => setLugaresSearch(e.target.value)}
                  placeholder="Buscar por nombre, ciudad, género o rol…"
                  ariaLabel="Buscar lugares"
                />
                {lugaresLoading ? (
                  <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>Cargando lugares verificados…</p>
                ) : lugaresFiltered.length === 0 ? (
                  <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>
                    No hay lugares con rol tienda, recogida o venue activo, o no coinciden con la búsqueda.
                  </p>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                    {lugaresFiltered.map(row => {
                      const thumb = mergePhotoGallery(row.logo_url, row.photos_urls)[0];
                      return (
                        <DCard
                          key={row.partner_id}
                          role="button"
                          tabIndex={0}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setLugarFicha(row);
                            }
                          }}
                          onClick={() => setLugarFicha(row)}
                          style={{
                            overflow: 'hidden',
                            cursor: 'pointer',
                            borderColor: row.featured_in_directory ? 'rgba(200,168,75,0.45)' : undefined,
                          }}
                        >
                          <div
                            style={{
                              height: 110,
                              background: thumb ? `#0b0f14 url(${thumb}) center/cover` : 'linear-gradient(135deg,#1a0d2e,#0d1a2e)',
                            }}
                          />
                          <div style={{ padding: '12px 14px' }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 6 }}>{row.company_name}</div>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>
                              {[row.city, row.country].filter(Boolean).join(' · ')}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {(row.place_roles || []).map(rk => (
                                <span
                                  key={rk}
                                  style={{
                                    fontSize: 9,
                                    padding: '2px 7px',
                                    borderRadius: 6,
                                    background: 'rgba(200,168,75,0.12)',
                                    color: C.gold,
                                  }}
                                >
                                  {PARTNER_ROLE_LABELS[rk] || rk}
                                </span>
                              ))}
                            </div>
                          </div>
                        </DCard>
                      );
                    })}
                  </div>
                )}
              </section>
            ) : (
            <>
            {heroSlide ? (
            <div
              style={{
                borderRadius: 14,
                marginBottom: 20,
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                outline: 'none',
                opacity: heroBlockOpacity,
                transition: slidingFeaturedHero ? 'opacity 0.45s ease-in-out' : undefined,
                background: '#0b0f14',
              }}
              onTransitionEnd={e => {
                if (e.propertyName !== 'opacity') return;
                if (!slidingFeaturedHero || heroFeaturedOpaque) return;
                setFeaturedHeroIdx(i => {
                  const n = heroPool.length;
                  return n <= 1 ? 0 : (i + 1) % n;
                });
                setHeroFeaturedOpaque(true);
              }}
            >
              <div
                style={{
                  flex: '0 0 auto',
                  height: 140,
                  width: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  background:
                    heroSlide?.isPromoAd && heroSlide.mediaType === 'video'
                      ? '#0b0f14'
                      : heroSlide?.imagenUrl
                        ? `#0b0f14 url(${heroSlide.imagenUrl}) center / contain no-repeat`
                        : 'linear-gradient(135deg,#1a0d2e,#0d1a2e)',
                }}
              >
                {heroSlide?.isPromoAd && heroSlide.mediaType === 'video' && heroSlide.mediaUrl ? (
                  <button
                    type="button"
                    aria-label={heroSlide.title ? `Abrir promoción: ${heroSlide.title}` : 'Abrir promoción'}
                    onClick={() => onHeroFeaturedImageClick(heroSlide)}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 1,
                      padding: 0,
                      margin: 0,
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <video
                      src={heroSlide.mediaUrl}
                      autoPlay
                      muted
                      loop
                      playsInline
                      style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', pointerEvents: 'none' }}
                    />
                  </button>
                ) : heroSlide?.imagenUrl ? (
                  <>
                    <button
                      type="button"
                      aria-label={
                        heroSlide.isPromoAd
                          ? heroSlide.title
                            ? `Abrir promoción: ${heroSlide.title}`
                            : 'Abrir promoción'
                          : heroSlide.title
                            ? `Ampliar cartel: ${heroSlide.title}`
                            : 'Ampliar cartel'
                      }
                      onClick={() => onHeroFeaturedImageClick(heroSlide)}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 1,
                        padding: 0,
                        margin: 0,
                        border: 'none',
                        background: 'transparent',
                        cursor: heroSlide.isPromoAd ? 'pointer' : 'zoom-in',
                      }}
                    />
                    <div
                      aria-hidden
                      style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 0,
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.35) 100%)',
                        pointerEvents: 'none',
                      }}
                    />
                    {!heroSlide.isPromoAd ? (
                      <span
                        style={{
                          position: 'absolute',
                          zIndex: 2,
                          top: 8,
                          left: 8,
                          fontSize: 9,
                          background: 'rgba(0,0,0,0.5)',
                          color: 'rgba(255,255,255,0.75)',
                          padding: '2px 7px',
                          borderRadius: 8,
                          pointerEvents: 'none',
                        }}
                      >
                        Ampliar
                      </span>
                    ) : null}
                  </>
                ) : (
                  <div
                    aria-hidden
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'radial-gradient(ellipse at 30% 60%, rgba(200,168,75,0.18) 0%, transparent 60%)',
                    }}
                  />
                )}
              </div>
              <div
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  e.preventDefault();
                  goToHeroPublication(heroSlide);
                }}
                onClick={() => goToHeroPublication(heroSlide)}
                style={{
                  flex: '0 0 auto',
                  position: 'relative',
                  zIndex: 1,
                  padding: '12px 16px 14px',
                  background: 'linear-gradient(180deg, #0d1218 0%, #0b0f14 100%)',
                  borderTop: '1px solid rgba(255,255,255,0.06)',
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    background: C.gold,
                    color: '#0a0a0f',
                    padding: '3px 9px',
                    borderRadius: 3,
                    fontSize: '0.65rem',
                    fontWeight: 900,
                    letterSpacing: '0.1em',
                    marginBottom: 8,
                    display: 'inline-block',
                  }}
                >
                  {heroSlide.isPromoAd ? 'Promoción' : 'Destacado'}
                </span>
                <h2
                  style={{
                    color: '#fff',
                    fontSize: '1.2rem',
                    fontWeight: 700,
                    margin: '4px 0 3px',
                    fontFamily: 'Georgia,serif',
                    lineHeight: 1.25,
                  }}
                >
                  {heroSlide.title}
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                  {heroSlide.isPromoAd
                    ? heroSlide.altText || 'DreamVinyl'
                    : [heroSlide.date, heroSlide.place, heroSlide.price].filter(Boolean).join(' · ')}
                </p>
                <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, marginTop: 6, marginBottom: 0 }}>
                  {heroSlide.isPromoAd
                    ? 'Pulsa el texto para abrir el enlace'
                    : heroSlide.imagenUrl
                      ? 'Pulsa la imagen para ampliar · el texto para ver en el listado'
                      : 'Pulsa para ver en el listado'}
                </p>
              </div>
            </div>
            ) : null}

            {sorteosList.length > 0 || sorteosLoading || sorteosError ? (
              <section
                id="sorteos-comunidad"
                aria-label="Sorteos oficiales"
                style={{ marginBottom: 22 }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  <div>
                    <p style={{ margin: 0, fontSize: 9, fontWeight: 800, letterSpacing: '0.18em', color: C.gold, textTransform: 'uppercase' }}>
                      Sorteos DreamVinyl
                    </p>
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.5)', maxWidth: 560, lineHeight: 1.45 }}>
                      Participa gratis o con grooves (según reglas de cada sorteo).
                    </p>
                  </div>
                  {sorteosLoading ? (
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>Cargando sorteos…</span>
                  ) : sorteosError ? (
                    <button
                      type="button"
                      onClick={() => reloadSorteos()}
                      style={{
                        fontSize: 11,
                        color: 'rgba(255,180,180,0.9)',
                        background: 'none',
                        border: 'none',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      Error al cargar · Reintentar
                    </button>
                  ) : null}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {sorteosList.map(s => {
                    const prod = typeof s.producto === 'object' && s.producto ? s.producto : {};
                    const emb = youtubeEmbedUrl(s.video_url);
                    const embRes = youtubeEmbedUrl(s.resultado_video_url);
                    const terminado = sorteoCelebrado(s);
                    const inscrOk = !terminado && sorteoInscripcionAbierta(s);
                    const ya = sorteoEnrolledIds.has(String(s.id));
                    const cost = s.modalidad_precio === 'grooves' ? Number(s.grooves_entrada || 0) : 0;
                    const ganadoresCadena = [
                      {
                        orden: 1,
                        label: 'Ganador principal',
                        username: s._ganador_principal_username,
                        id: s.ganador_principal_user_id,
                      },
                      {
                        orden: 2,
                        label: 'Suplente (si no localiza/primero)',
                        username: s._ganador_suplente_2_username,
                        id: s.ganador_suplente_2_user_id,
                      },
                      {
                        orden: 3,
                        label: 'Suplente (si no responde el 2º)',
                        username: s._ganador_suplente_3_username,
                        id: s.ganador_suplente_3_user_id,
                      },
                    ].filter(g => g.id);
                    return (
                      <DCard key={s.id} style={{ overflow: 'hidden' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                          {s.imagen_url ? (
                            <div
                              role="presentation"
                              style={{ width: 'min(100%,260px)', minHeight: 180, flexShrink: 0, background: `#0d0f14 url(${s.imagen_url}) center/cover` }}
                            />
                          ) : null}
                          <div style={{ padding: '14px 16px', minWidth: 0, flex: '1 1 220px' }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: terminado ? '#5DCAA5' : C.gold, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
                              {terminado
                                ? 'Sorteo terminado'
                                : s.estado === 'cerrado'
                                  ? 'Sorteo cerrado'
                                  : 'Sorteo abierto oficial'}
                            </div>
                            <h3 style={{ margin: '0 0 8px', fontSize: '1.08rem', color: '#fff', fontFamily: 'Georgia,serif' }}>{s.titulo}</h3>
                            {s.descripcion ? (
                              <p style={{ margin: '0 0 10px', fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>
                                {s.descripcion}
                              </p>
                            ) : null}
                            {(prod.artista || prod.titulo_disco) ? (
                              <div style={{ fontSize: 11, color: 'rgba(200,168,75,0.85)', marginBottom: 10, lineHeight: 1.5 }}>
                                {prod.artista ? <span><strong>Artista</strong> · {prod.artista}{' · '}</span> : null}
                                {prod.titulo_disco ? <span><strong>Disco</strong> · {prod.titulo_disco}{' · '}</span> : null}
                                {prod.anio ? <span><strong>Año</strong> · {prod.anio}{' · '}</span> : null}
                                {prod.formato ? <span><strong>Formato</strong> · {prod.formato}</span> : null}
                                {prod.condicion ? (
                                  <div style={{ marginTop: 6, color: 'rgba(255,255,255,0.45)' }}>
                                    <strong>Estado</strong> · {prod.condicion}{prod.sello ? ` · Sello · ${prod.sello}` : ''}
                                  </div>
                                ) : prod.sello ? (
                                  <div style={{ marginTop: 6, color: 'rgba(255,255,255,0.45)' }}>Sello · {prod.sello}</div>
                                ) : null}
                                {prod.notas ? (
                                  <div style={{ marginTop: 8, fontSize: 10, color: 'rgba(255,255,255,0.38)', fontStyle: 'italic' }}>
                                    {prod.notas}
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10 }}>
                              <strong style={{ color: 'rgba(255,255,255,0.6)' }}>Sorteo:</strong>{' '}
                              {s.fecha_sorteo ? new Date(s.fecha_sorteo).toLocaleString('es-ES') : '—'}{' · '}
                              <strong style={{ color: 'rgba(255,255,255,0.6)' }}> Inscripción</strong> hasta{' '}
                              {s.inscripciones_hasta ? new Date(s.inscripciones_hasta).toLocaleString('es-ES') : '—'}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                              <span style={{ fontSize: 10, padding: '4px 10px', borderRadius: 20, border: `0.5px solid ${C.gold}`, color: C.gold, background: 'rgba(200,168,75,0.1)' }}>
                                {cost > 0 ? `Entrada ${cost} grooves` : 'Participación gratuita'}
                              </span>
                              <span style={{ fontSize: 10, padding: '4px 10px', borderRadius: 20, background: 'rgba(255,255,255,0.06)', border: '0.5px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}>
                                {typeof s.inscripciones_count === 'number' ? `${s.inscripciones_count} inscritos` : ' — inscritos'}
                                {' · '}
                                {typeof s.ganadores_previstos === 'number' && s.ganadores_previstos > 1
                                  ? `${s.ganadores_previstos} premios previstos`
                                  : 'Cadena 1+2 supl. si hiciera falta'}
                              </span>
                              {user?.id ? (
                                groovesComunidadPhoneOk ? (
                                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
                                    Tu saldo: <strong style={{ color: C.gold }}>{userGroovesComunidad != null ? userGroovesComunidad : '—'}</strong> grooves
                                  </span>
                                ) : (
                                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', display: 'inline-flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                                    <span>{GROOVES_PHONE_GATE_USER_MESSAGE}</span>
                                    <button
                                      type="button"
                                      onClick={() => onNavigate('ajustes', { initialSub: 'cuenta' })}
                                      style={{
                                        padding: '4px 10px',
                                        borderRadius: 8,
                                        border: `0.5px solid ${C.gold}`,
                                        background: 'rgba(200,168,75,0.12)',
                                        color: C.gold,
                                        fontSize: 10,
                                        cursor: 'pointer',
                                      }}>
                                      Ir a Ajustes
                                    </button>
                                  </span>
                                )
                              ) : null}
                            </div>
                            {terminado && !ganadoresCadena.length ? (
                              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', margin: '0 0 12px', lineHeight: 1.5 }}>
                                Este sorteo ya se celebró. Los ganadores se publicarán aquí en cuanto estén confirmados.
                              </p>
                            ) : null}
                            {ganadoresCadena.length ? (
                                <div
                                  style={{
                                    marginBottom: 12,
                                    padding: '10px 12px',
                                    borderRadius: 10,
                                    background: 'rgba(29,158,117,0.09)',
                                    border: '1px solid rgba(29,158,117,0.28)',
                                  }}
                                >
                                  <div style={{ fontSize: 10, fontWeight: 700, color: '#5DCAA5', marginBottom: 8 }}>
                                    {terminado
                                      ? 'Ganadores'
                                      : 'Orden oficial de llamada al premio (1 disco / un titular + reservas)'}
                                  </div>
                                  <ol style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: 'rgba(255,255,255,0.88)', lineHeight: 1.55 }}>
                                    {ganadoresCadena.map(g => (
                                      <li key={g.id} style={{ marginBottom: 4 }}>
                                        <strong style={{ color: 'rgba(255,255,255,0.7)' }}>
                                          {(terminado && g.orden === 1 ? 'Ganador principal' : terminado ? g.label : `${g.orden}. ${g.label}`)}:
                                        </strong>{' '}
                                        @{g.username || 'usuario'}
                                      </li>
                                    ))}
                                  </ol>
                                  {!terminado ? (
                                    <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.38)', margin: '8px 0 0', lineHeight: 1.45 }}>
                                      Primero contacto al ganador principal; si no se localiza o no responde en el plazo que marque DreamVinyl, pasamos al{' '}
                                      suplente 2 y después al 3, según bases publicadas.
                                    </p>
                                  ) : null}
                                </div>
                            ) : null}
                            {emb ? (
                              <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, marginBottom: 12, borderRadius: 12, overflow: 'hidden', background: '#000', maxWidth: 480 }}>
                                <iframe title={s.titulo} src={`${emb}?rel=0`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" loading="lazy" />
                              </div>
                            ) : null}
                            {embRes ? (
                              <div style={{ marginBottom: 10 }}>
                                <p style={{ fontSize: 10, color: '#5DCAA5', margin: '0 0 8px', fontWeight: 600 }}>Vídeo / resultado público</p>
                                <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: 12, overflow: 'hidden', background: '#000', maxWidth: 480 }}>
                                  <iframe title={`Resultado — ${s.titulo}`} src={`${embRes}?rel=0`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" loading="lazy" />
                                </div>
                              </div>
                            ) : null}
                            {s.legal_texto ? (
                              <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.32)', lineHeight: 1.5, margin: '0 0 8px' }}>{s.legal_texto}</p>
                            ) : null}
                            {s.legal_url ? (
                              <a href={hrefMaps(s.legal_url)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: C.gold, marginBottom: 10, display: 'inline-block' }}>
                                Bases y condiciones →
                              </a>
                            ) : null}
                            <div style={{ marginTop: 8 }}>
                              {terminado ? (
                                ya ? (
                                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>
                                    Participaste en este sorteo. Consulta arriba el resultado.
                                  </span>
                                ) : null
                              ) : ya ? (
                                <span style={{ fontSize: 11, color: '#5DCAA5', fontWeight: 600 }}>Ya participas en este sorteo</span>
                              ) : inscrOk ? (
                                <button
                                  type="button"
                                  disabled={sorteoInscribirBusyId === String(s.id)}
                                  onClick={() => onInscribirSorteo(s)}
                                  style={{
                                    padding: '8px 16px',
                                    borderRadius: 10,
                                    border: 'none',
                                    background: `linear-gradient(135deg, ${C.gold}, #a8883b)`,
                                    color: '#0a0a0f',
                                    fontWeight: 800,
                                    fontSize: 12,
                                    cursor: sorteoInscribirBusyId === String(s.id) ? 'wait' : 'pointer',
                                    opacity: sorteoInscribirBusyId === String(s.id) ? 0.7 : 1,
                                  }}
                                >
                                  {sorteoInscribirBusyId === String(s.id)
                                    ? 'Inscribiendo…'
                                    : cost > 0
                                      ? `Participar (${cost} grooves)`
                                      : 'Participar gratis'}
                                </button>
                              ) : s.estado === 'cerrado' ? (
                                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.42)' }}>
                                  Inscripciones cerradas. El sorteo se celebrará en la fecha indicada.
                                </span>
                              ) : (
                                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.42)' }}>
                                  Las inscripciones no están abiertas ahora mismo.
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </DCard>
                    );
                  })}
                </div>
              </section>
            ) : null}

            <ComunidadSearchInput
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar por título, ciudad, género musical, lugar…"
              ariaLabel="Buscar en comunidad"
            />

            <div style={{ display:'flex', gap:7, overflowX:'auto', marginBottom:20 }}>
              {!showHistorico ? (
                <>
                  {CHIPS.map(f => {
                    const active = activeChip === f;
                    return (
                      <button
                        key={f}
                        type="button"
                        onClick={() => setActiveChip(f)}
                        style={{
                          padding:'5px 13px', borderRadius:20, fontSize:11, whiteSpace:'nowrap', cursor:'pointer', flexShrink:0,
                          border: active ? `0.5px solid ${C.gold}` : '0.5px solid rgba(255,255,255,0.15)',
                          color: active ? C.gold : 'rgba(255,255,255,0.65)',
                          background: active ? 'rgba(200,168,75,0.12)' : 'transparent',
                        }}
                      >
                        {f}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => {
                      setShowHistorico(true);
                      setHistoricoTipo('Todo');
                    }}
                    style={{
                      padding:'5px 13px', borderRadius:20, fontSize:11, whiteSpace:'nowrap', cursor:'pointer', flexShrink:0,
                      border: '0.5px solid rgba(255,255,255,0.15)',
                      color: 'rgba(255,255,255,0.65)',
                      background: 'transparent',
                    }}
                  >
                    Histórico
                  </button>
                </>
              ) : (
                <>
                  {['Todo', 'Oficial', 'Comunidad', 'Lugar'].map(f => {
                    const active = historicoTipo === f;
                    return (
                      <button
                        key={f}
                        type="button"
                        onClick={() => setHistoricoTipo(f)}
                        style={{
                          padding:'5px 13px', borderRadius:20, fontSize:11, whiteSpace:'nowrap', cursor:'pointer', flexShrink:0,
                          border: active ? `0.5px solid ${C.gold}` : '0.5px solid rgba(255,255,255,0.15)',
                          color: active ? C.gold : 'rgba(255,255,255,0.65)',
                          background: active ? 'rgba(200,168,75,0.12)' : 'transparent',
                        }}
                      >
                        {f}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setShowHistorico(false)}
                    style={{
                      padding:'5px 13px', borderRadius:20, fontSize:11, whiteSpace:'nowrap', cursor:'pointer', flexShrink:0,
                      border: '0.5px solid rgba(255,255,255,0.15)',
                      color: 'rgba(255,255,255,0.65)',
                      background: 'transparent',
                    }}
                  >
                    Volver
                  </button>
                </>
              )}
            </div>

            <div style={{ fontSize:13, fontWeight:500, color:'#fff', marginBottom:12, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
              <span>{showHistorico ? 'Histórico de eventos' : 'Eventos destacados'}</span>
              {hasFeedFilters ? (
                <span style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>{filtered.length} resultado{filtered.length === 1 ? '' : 's'}</span>
              ) : null}
            </div>
            {filtered.length === 0 ? (
              <div style={{ textAlign:'center', padding:'36px 16px', color:'rgba(255,255,255,0.45)', fontSize:14 }}>
                {!feedInitialDone ? (
                  'Cargando publicaciones…'
                ) : feedError ? (
                  <>
                    {feedError}{' '}
                    <button
                      type="button"
                      onClick={() => reloadFeed()}
                      style={{ background: 'none', border: 'none', color: C.gold, textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      Reintentar
                    </button>
                  </>
                ) : hasFeedFilters ? (
                  <>
                    No hay resultados. Prueba otras palabras o filtros, o{' '}
                    <button
                      type="button"
                      onClick={() => { setSearch(''); setActiveChip('Todo'); setHistoricoTipo('Todo'); setShowHistorico(false); }}
                      style={{ background:'none', border:'none', color:C.gold, textDecoration:'underline', cursor:'pointer' }}
                    >
                      restablece la búsqueda
                    </button>
                    .
                  </>
                ) : showHistorico ? (
                  'No hay eventos en el histórico.'
                ) : dbEvents.length === 0 ? (
                  <>
                    Aún no hay publicaciones en Comunidad.{' '}
                    <button type="button" onClick={abrirPublicar} style={{ background:'none', border:'none', color:C.gold, textDecoration:'underline', cursor:'pointer' }}>
                      Publica el primero
                    </button>
                    .
                  </>
                ) : (
                  'No hay eventos próximos visibles. Revisa el histórico o publica uno nuevo.'
                )}
              </div>
            ) : (
              filtered.map(ev => (
                <DCard
                  key={ev.id}
                  data-comunidad-id={ev._db ? String(ev.id) : undefined}
                  style={{ overflow:'hidden', marginBottom:14 }}
                >
                  {ev.imagenUrl ? (
                    <div style={comunidadFeedMediaWrap}>
                      <button
                        type="button"
                        aria-label={ev.title ? `Ampliar cartel: ${ev.title}` : 'Ampliar cartel'}
                        onClick={() =>
                          setPosterLightbox({ url: ev.imagenUrl, title: ev.title || null })
                        }
                        style={{
                          position: 'absolute',
                          inset: 0,
                          zIndex: 0,
                          padding: 0,
                          margin: 0,
                          border: 'none',
                          background: 'transparent',
                          cursor: 'zoom-in',
                        }}
                      >
                        <img
                          src={ev.imagenUrl}
                          alt=""
                          decoding="async"
                          loading="lazy"
                          style={comunidadFeedMediaImg}
                        />
                      </button>
                      <div
                        style={{
                          position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1,
                          background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.78) 100%)',
                        }}
                      />
                      <span style={{ position: 'relative', zIndex: 2, ...comunidadFeedTagPill, pointerEvents: 'none' }}>{ev.tag}</span>
                      <span style={{ position: 'absolute', zIndex: 2, top:8, right:8, fontSize:9, background:'rgba(0,0,0,0.55)', color:'rgba(255,255,255,0.9)', padding:'2px 7px', borderRadius:8, backdropFilter:'blur(2px)', pointerEvents: 'none' }}>{ev.price}</span>
                      <span
                        style={{
                          position: 'absolute',
                          zIndex: 2,
                          top: 8,
                          left: 8,
                          fontSize: 9,
                          background: 'rgba(0,0,0,0.5)',
                          color: 'rgba(255,255,255,0.75)',
                          padding: '2px 7px',
                          borderRadius: 8,
                          pointerEvents: 'none',
                        }}
                      >
                        Ampliar
                      </span>
                    </div>
                  ) : (
                    <div
                      style={{
                        height: COMUNIDAD_FEED_MEDIA_HEIGHT,
                        background: `linear-gradient(135deg, ${ev.color}, rgba(0,0,0,0.3))`,
                        display: 'flex',
                        alignItems: 'flex-end',
                        padding: '10px 14px',
                        position: 'relative',
                      }}
                    >
                      <span style={comunidadFeedTagPill}>{ev.tag}</span>
                      <span style={{ position:'absolute', top:8, right:8, fontSize:9, background:'rgba(0,0,0,0.5)', color:'rgba(255,255,255,0.7)', padding:'2px 7px', borderRadius:8 }}>{ev.price}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch' }}>
                    <div style={{ flex: '1 1 auto', minWidth: 0, padding: '10px 14px' }}>
                    {ev.extraTipo && (
                      <div style={{ fontSize:10, color:'rgba(200,168,75,0.8)', marginBottom:4, fontWeight:600 }}>{ev.extraTipo}</div>
                    )}
                    <ComunidadGenrePills genres={ev.musicGenres} compact />
                    <div style={{ fontSize:14, fontWeight:500, color:'#fff', marginBottom:5 }}>{ev.title}</div>
                    {isAdmin && ev._db ? (
                      <div style={{ marginBottom: 6 }}>
                        {(() => {
                          const est = comunidadModeracionEstado({
                            aprobada: ev.aprobada,
                            isHidden: ev.isHidden,
                            metadata: ev.metadata,
                          });
                          return (
                            <span
                              style={comunidadModeracionEstadoPillStyle(est.key, {
                                gold: C.gold,
                                green: C.green,
                                danger: C.danger,
                              })}
                              title={est.title}
                            >
                              {est.label}
                            </span>
                          );
                        })()}
                      </div>
                    ) : null}
                    {!isAdmin && ev._db && !ev.aprobada && !ev.isHidden ? (
                      <div
                        style={{
                          display: 'inline-block',
                          marginBottom: 6,
                          fontSize: 10,
                          padding: '3px 8px',
                          borderRadius: 8,
                          background: 'rgba(200,168,75,0.12)',
                          border: '1px solid rgba(200,168,75,0.35)',
                          color: C.gold,
                        }}
                      >
                        Pendiente de moderación del equipo — aún no visible para todos en el feed
                      </div>
                    ) : null}
                    {ev._db && ev.aprobada && !ev.isHidden && ev.destacada ? (
                      <div
                        style={{
                          display: 'inline-block',
                          marginBottom: 6,
                          fontSize: 10,
                          padding: '3px 8px',
                          borderRadius: 8,
                          background: 'rgba(29,158,117,0.14)',
                          border: '1px solid rgba(29,158,117,0.35)',
                          color: C.green,
                        }}
                      >
                        En portada destacada (rota con otras si hay varias)
                      </div>
                    ) : null}
                    {ev._db &&
                    ev.aprobada &&
                    !ev.isHidden &&
                    partnerApproved &&
                    ev.destacadaSolicitudPendiente &&
                    !ev.destacada ? (
                      <div
                        style={{
                          display: 'inline-block',
                          marginBottom: 6,
                          fontSize: 10,
                          padding: '3px 8px',
                          borderRadius: 8,
                          background: 'rgba(200,168,75,0.1)',
                          border: '1px solid rgba(200,168,75,0.28)',
                          color: 'rgba(230,208,140,0.95)',
                        }}
                      >
                        Solicitud de portada pendiente de moderación
                      </div>
                    ) : null}
                    {ev.entrada_tipo != null && (
                      <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:6, alignItems:'center' }}>
                        {renderEntradaTipoPill(ev)}
                      </div>
                    )}
                    {ev.notas && <div style={{ fontSize:11, color:'rgba(255,255,255,0.38)', marginBottom:8, lineHeight:1.4 }}>{ev.notas}</div>}
                    <div style={{ display:'flex', flexWrap:'wrap', gap:12, marginBottom:8, alignItems:'flex-start' }}>
                      {[[ev.date,'calendar'],[ev.place,'map']].map(([v,ic]) => (
                        <div key={ic} style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'rgba(255,255,255,0.45)', maxWidth:'100%' }}>
                          <Icon name={ic} size={10} color="rgba(255,255,255,0.45)"/>
                          <span style={{ wordBreak:'break-word' }}>{v}</span>
                        </div>
                      ))}
                    </div>
                    {ev.mapsUrl ? (
                      <a
                        href={hrefMaps(ev.mapsUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:10, color: C.gold, textDecoration:'none', marginTop:2, marginBottom:2, fontWeight:600 }}
                      >
                        Abrir en Google Maps <span style={{ fontSize:9, opacity:0.85 }}>↗</span>
                      </a>
                    ) : null}
                    {ev.entrada_tipo != null && ev.entrada_enlace && coalesceEntradasVerif(ev.entradas_verificadas) && (
                      <div style={{ marginTop:6, marginBottom:2 }}>
                        <button
                          type="button"
                          onClick={() => window.open(hrefTicket(ev.entrada_enlace), '_blank', 'noopener,noreferrer')}
                          style={{
                            background: 'linear-gradient(135deg, #c8a84b, #a8883b)',
                            color: '#0a0a0f', fontWeight: 700, fontSize: 12, border: 'none', borderRadius: 6,
                            padding: '6px 14px', cursor: 'pointer',
                          }}
                        >🎟 Comprar entradas
                        </button>
                      </div>
                    )}
                    {ev.entrada_tipo != null && ev.entrada_enlace && !coalesceEntradasVerif(ev.entradas_verificadas) && (
                      <div
                        style={{
                          display: 'inline-block', marginTop: 6, fontSize: 10, padding: '4px 9px', borderRadius: 8,
                          background: 'rgba(239,159,39,0.15)', border: '1px solid rgba(239,159,39,0.4)', color: '#EF9F27',
                        }}
                      >⏳ Entradas pendientes de verificación
                      </div>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, paddingTop: 8, borderTop: '0.5px solid rgba(255,255,255,0.06)' }}>
                      {[
                        ['check', 'Asistiré', `asistiré-${ev.id}`, ev.asistiré],
                        ['heart', 'Me interesa', `interesa-${ev.id}`, ev.interesa],
                      ].map(([ic, lbl, key, count]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => toggleReaction(key)}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: reactions[key] ? C.gold : 'rgba(255,255,255,0.45)', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
                        >
                          <Icon name={ic} size={12} color={reactions[key] ? C.gold : 'rgba(255,255,255,0.45)'}/> {lbl}
                          {count > 0 ? ` (${count + (reactions[key] ? 1 : 0)})` : reactions[key] ? ' (1)' : ''}
                        </button>
                      ))}
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <button
                          type="button"
                          onClick={() => toggleGuardar(ev)}
                          disabled={savingId === String(ev.id)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 4, fontSize: 10,
                            color: (user?.id && savedItemIds.has(String(ev.id))) ? C.gold : 'rgba(255,255,255,0.45)',
                            cursor: savingId === String(ev.id) ? 'wait' : 'pointer', background: 'none', border: 'none', padding: 0, fontWeight: (user?.id && savedItemIds.has(String(ev.id))) ? 600 : 400,
                            opacity: savingId === String(ev.id) ? 0.6 : 1,
                          }}
                          title={user?.id ? (savedItemIds.has(String(ev.id)) ? 'Quitar de guardados' : 'Guardar en tu perfil') : 'Inicia sesión para guardar'}
                        >
                          <Icon name="bookmark" size={12} color={(user?.id && savedItemIds.has(String(ev.id))) ? C.gold : 'rgba(255,255,255,0.45)'} />
                          {user?.id && savedItemIds.has(String(ev.id)) ? 'Guardado' : 'Guardar'}
                        </button>
                        <button
                          type="button"
                          onClick={() => compartirEvento(ev)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 4, fontSize: 10,
                            color: shareCopiedId === ev.id ? C.gold : 'rgba(255,255,255,0.45)',
                            cursor: 'pointer', background: 'none', border: 'none', padding: 0, fontWeight: shareCopiedId === ev.id ? 600 : 400,
                          }}
                        >
                          <Icon name="share" size={12} color={shareCopiedId === ev.id ? C.gold : 'rgba(255,255,255,0.45)'}/> Compartir
                        </button>
                        {shareCopiedId === ev.id ? (
                          <span style={{ fontSize: 9, color: C.gold, whiteSpace: 'nowrap' }}>· Enlace copiado</span>
                        ) : null}
                      </div>
                    </div>
                    {ev.authorUsername ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                        <Avatar
                          initials={profileInitials(ev.authorUsername)}
                          size={20}
                          bg="rgba(200,168,75,0.15)"
                          color={C.gold}
                          imageUrl={ev.authorAvatarUrl}
                        />
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)' }}>@{ev.authorUsername}</div>
                      </div>
                    ) : null}
                    </div>
                    {((user?.id && ev.authorId && ev.authorId === user.id) || (user?.id && isAdmin && ev._db)) ? (
                      <div
                        style={{
                          flexShrink: 0,
                          width: 156,
                          maxWidth: '36%',
                          boxSizing: 'border-box',
                          borderLeft: '1px solid rgba(255,255,255,0.06)',
                          padding: '10px 10px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                          alignItems: 'stretch',
                          alignSelf: 'stretch',
                          background: 'rgba(0,0,0,0.16)',
                        }}
                      >
                        {user?.id && ev.authorId && ev.authorId === user.id ? (
                          <>
                            <button
                              type="button"
                              onClick={() => abrirEditar(ev)}
                              style={{
                                width: '100%',
                                boxSizing: 'border-box',
                                padding: '8px 8px',
                                borderRadius: 8,
                                border: '0.5px solid rgba(200,168,75,0.35)',
                                background: 'rgba(200,168,75,0.08)',
                                color: C.gold,
                                fontSize: 10,
                                fontWeight: 600,
                                cursor: 'pointer',
                                textAlign: 'center',
                                lineHeight: 1.25,
                              }}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => onEliminarPublicacion(ev)}
                              style={{
                                width: '100%',
                                boxSizing: 'border-box',
                                padding: '8px 8px',
                                borderRadius: 8,
                                border: '0.5px solid rgba(226,75,74,0.4)',
                                background: 'transparent',
                                color: 'rgba(255,180,180,0.95)',
                                fontSize: 10,
                                fontWeight: 600,
                                cursor: 'pointer',
                                textAlign: 'center',
                                lineHeight: 1.25,
                              }}
                            >
                              Eliminar
                            </button>
                            {partnerApproved && ev.aprobada && !ev.isHidden && !ev.destacada ? (
                              ev.destacadaSolicitudPendiente ? (
                                <button
                                  type="button"
                                  disabled={destacBusyId === String(ev.id)}
                                  onClick={() => onCancelarDestacado(ev)}
                                  style={{
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    padding: '8px 8px',
                                    borderRadius: 8,
                                    border: '0.5px solid rgba(255,255,255,0.2)',
                                    background: 'rgba(255,255,255,0.05)',
                                    color: 'rgba(255,255,255,0.75)',
                                    fontSize: 10,
                                    fontWeight: 600,
                                    cursor: destacBusyId === String(ev.id) ? 'wait' : 'pointer',
                                    opacity: destacBusyId === String(ev.id) ? 0.65 : 1,
                                    textAlign: 'center',
                                    lineHeight: 1.25,
                                  }}
                                >
                                  Cancelar solicitud portada
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  disabled={destacBusyId === String(ev.id)}
                                  onClick={() => onSolicitarDestacado(ev)}
                                  style={{
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    padding: '8px 8px',
                                    borderRadius: 8,
                                    border: `0.5px solid ${C.gold}`,
                                    background: 'rgba(200,168,75,0.12)',
                                    color: C.gold,
                                    fontSize: 10,
                                    fontWeight: 600,
                                    cursor: destacBusyId === String(ev.id) ? 'wait' : 'pointer',
                                    opacity: destacBusyId === String(ev.id) ? 0.65 : 1,
                                    textAlign: 'center',
                                    lineHeight: 1.25,
                                  }}
                                  title={`Si un admin te destaca en portada, se pueden descontar ${GROOVES.COMUNIDAD_DESTACAR_PARTNER} grooves`}
                                >
                                  Solicitar portada ({GROOVES.COMUNIDAD_DESTACAR_PARTNER} G si aprueban)
                                </button>
                              )
                            ) : null}
                          </>
                        ) : null}
                        {user?.id && isAdmin && ev._db ? (
                          <>
                            <div
                              style={{
                                height: user?.id && ev.authorId && ev.authorId === user.id ? 1 : 0,
                                margin: user?.id && ev.authorId && ev.authorId === user.id ? '2px 0 0' : 0,
                                background: 'rgba(255,255,255,0.08)',
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => onAdminToggleHidden(ev)}
                              style={{
                                width: '100%',
                                boxSizing: 'border-box',
                                padding: '8px 8px',
                                borderRadius: 8,
                                border: '0.5px solid rgba(255,255,255,0.15)',
                                background: 'rgba(255,255,255,0.04)',
                                color: 'rgba(255,255,255,0.7)',
                                fontSize: 10,
                                fontWeight: 600,
                                cursor: 'pointer',
                                textAlign: 'center',
                                lineHeight: 1.25,
                              }}
                            >
                              {ev.isHidden ? 'Mostrar' : 'Ocultar'}
                            </button>
                            <button
                              type="button"
                              onClick={() => onAdminEliminar(ev)}
                              style={{
                                width: '100%',
                                boxSizing: 'border-box',
                                padding: '8px 8px',
                                borderRadius: 8,
                                border: '0.5px solid rgba(226,75,74,0.4)',
                                background: 'transparent',
                                color: 'rgba(255,180,180,0.95)',
                                fontSize: 10,
                                fontWeight: 600,
                                cursor: 'pointer',
                                textAlign: 'center',
                                lineHeight: 1.25,
                              }}
                            >
                              Eliminar (admin)
                            </button>
                          </>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </DCard>
              ))
            )}
            </>
            )}
          </main>

          {!(isMobile || isTablet) ? (
            <aside style={{ position:'sticky', top:70, display:'flex', flexDirection:'column', gap:12, alignSelf:'start' }}>
            <div style={{ borderRadius:12, border:'none', background:'transparent', padding:0, margin:0, overflow:'hidden', minHeight:320, display:'flex', flexDirection:'column', alignItems:'stretch', justifyContent:'stretch' }}>
              <AdSlot slot="lateral_comunidad" />
            </div>
            <div
              style={{
                borderRadius:14,
                border:'1px solid rgba(200,168,75,0.28)',
                background:'linear-gradient(155deg, rgba(200,168,75,0.14) 0%, rgba(18,16,28,0.92) 42%, rgba(10,10,15,0.98) 100%)',
                padding:'18px 16px 16px',
                textAlign:'center',
                boxShadow:'0 12px 40px rgba(0,0,0,0.35)',
              }}>
              <div style={{ width:44, height:44, borderRadius:12, margin:'0 auto 12px', background:'rgba(200,168,75,0.15)', border:'0.5px solid rgba(200,168,75,0.35)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <Icon name="star" size={22} color={C.gold} />
              </div>
              <div style={{ fontSize:13, fontWeight:800, color:'#fff', letterSpacing:'0.04em', marginBottom:4 }}>
                Próximos sorteos DreamVinyl
              </div>
              <div style={{ fontSize:9, color:C.gold, fontWeight:700, letterSpacing:'0.14em', marginBottom:12 }}>COMUNIDAD</div>
              <p style={{ fontSize:11, color:'rgba(255,255,255,0.55)', lineHeight:1.55, margin:0, marginBottom:14 }}>
                Aquí anunciaremos sorteos y regalos exclusivos para la comunidad. Mientras tanto, sigue el surco: muy pronto habrá novedades.
              </p>
              <div
                style={{
                  fontSize:9,
                  padding:'6px 12px',
                  borderRadius:999,
                  background:'rgba(255,255,255,0.06)',
                  color:'rgba(255,255,255,0.5)',
                  fontWeight:600,
                  letterSpacing:'0.06em',
                  display:'inline-block',
                  border:'0.5px solid rgba(255,255,255,0.08)',
                }}>
                Próximamente
              </div>
            </div>
            </aside>
          ) : null}
        </div>
      </div>

      <ComunidadPosterLightbox
        open={!!posterLightbox}
        imageUrl={posterLightbox?.url ?? null}
        title={posterLightbox?.title ?? null}
        onClose={() => setPosterLightbox(null)}
      />
    </div>
  );
}

const inputBase = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.12)',
  background: 'rgba(0,0,0,0.25)',
  color: '#fff',
  fontSize: 13,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};

const selEntrada = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid #1e2d3d',
  background: '#16202e',
  color: '#e8edf2',
  fontSize: 13,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
  cursor: 'pointer',
};

const subInpEntrada = {
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #1e2d3d',
  background: '#16202e',
  color: '#e8edf2',
  fontSize: 12,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};

const subInpEnlaceVenta = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #1e2d3d',
  background: '#16202e',
  color: '#e8edf2',
  fontSize: 12,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};

const btnPrimary = {
  padding: '10px 20px',
  borderRadius: 10,
  border: 'none',
  background: C.gold,
  color: '#0a0a0f',
  fontWeight: 800,
  fontSize: 13,
  cursor: 'pointer',
};

const btnSecondary = {
  padding: '10px 18px',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.2)',
  background: 'transparent',
  color: 'rgba(255,255,255,0.8)',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};
